import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import CodeBlock from '../../components/CodeBlock';
import { useProblem } from '../../hooks/useProblem';
import './ProblemPage.css';
// import WalkthroughRenderer from "../../components/walkthroughs/WalkthroughRenderer";
/* ─────────────────────────────────────────────
   TABLE OF CONTENTS
   Desktop/tablet: sticky left sidebar
   Mobile:         floating hamburger + bottom drawer
───────────────────────────────────────────── */
const TOC_ITEMS = [
  { id: 'sec-video',      label: 'Video',              icon: '▶'   },
  { id: 'sec-what',       label: 'What Are We Doing',  icon: '❓'  },
  { id: 'sec-concept',    label: 'Underlying Concept', icon: '💡'  },
  { id: 'sec-pattern',    label: 'Pattern Recognition',icon: '🔍'  },
  { id: 'sec-visual',     label: 'Visual Flow',        icon: '🎬'  },
  { id: 'sec-code',       label: 'Code',               icon: '</>' },
  { id: 'sec-complexity', label: 'Complexity',         icon: '⏱'  },
  { id: 'sec-similar',    label: 'Similar Problems',   icon: '🔗'  },
];

function TableOfContents() {
  const [active, setActive] = useState('sec-video');
  const [open,   setOpen]   = useState(false);
  const drawerRef           = useRef(null);

  /* highlight whichever section is currently in viewport */
  useEffect(() => {
    const observers = [];
    TOC_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(id); },
        { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  /* close drawer when clicking outside */
  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const scrollTo = id => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOpen(false);
  };

  const NavList = () => (
    <nav className="toc__nav">
      <div className="toc__nav-title">On this page</div>
      {TOC_ITEMS.map(({ id, label, icon }) => (
        <button
          key={id}
          data-label={label}
          className={`toc__item ${active === id ? 'toc__item--on' : ''}`}
          onClick={() => scrollTo(id)}
        >
          <span className="toc__item-icon">{icon}</span>
          <span className="toc__item-label">{label}</span>
          {active === id && <span className="toc__item-pip"/>}
        </button>
      ))}
    </nav>
  );

  return (
    <>
      {/* DESKTOP / TABLET ── sticky sidebar */}
      <aside className="toc__sidebar">
        <NavList/>
      </aside>

      {/* MOBILE ── floating hamburger + slide-up drawer */}
      <div className="toc__mobile">
        <button
          className={`toc__fab ${open ? 'toc__fab--open' : ''}`}
          onClick={() => setOpen(o => !o)}
          aria-label="Navigation menu"
        >
          <span className="toc__fab-bar toc__fab-bar--1"/>
          <span className="toc__fab-bar toc__fab-bar--2"/>
          <span className="toc__fab-bar toc__fab-bar--3"/>
        </button>

        <div className={`toc__backdrop ${open ? 'toc__backdrop--on' : ''}`} onClick={() => setOpen(false)}/>

        <div ref={drawerRef} className={`toc__drawer ${open ? 'toc__drawer--on' : ''}`}>
          <div className="toc__drawer-pill"/>
          <NavList/>
        </div>
      </div>
    </>
  );
}

/* ── skeleton ── */
function SK({ h='14px', w='100%', mb='8px' }) {
  return <div className="skel" style={{height:h,width:w,marginBottom:mb}}/>;
}

/* ── universal visual walkthrough ── */
function Visual({ steps }) {
  const [s, setS] = useState(0);
  if (!steps?.length) return null;
  const cur = steps[s], total = steps.length;
  const go = n => setS(Math.max(0, Math.min(total-1, n)));

  return (
    <div className="viz">

      {/* ── Progress dots ── */}
      <div className="viz__prog">
        <span className="viz__prog-txt">step <strong>{s}</strong> / {total-1}</span>
        <div className="viz__dots">
          {steps.map((_,i) => (
            <button key={i} onClick={() => go(i)}
              className={`viz__dot ${i===s?'viz__dot--on':i<s?'viz__dot--done':''}`}/>
          ))}
        </div>
      </div>

{cur.type === 'rotate_matrix_transform' && (() => {
  const cols = cur.matrix?.[0]?.length ?? 3;
  return (
    <div className="viz__rotate">
      <div className="viz__rotate-phase">{cur.phase}</div>
      <div
        className="viz__rotate-grid"
        style={{ gridTemplateColumns: `repeat(${cols}, 70px)` }}
      >
        {cur.matrix?.map((row, r) =>
          row.map((cell, c) => {
            const swapped = cur.swap &&
              ((r === cur.swap[0][0] && c === cur.swap[0][1]) ||
               (r === cur.swap[1][0] && c === cur.swap[1][1]));
            const reversed = cur.reverse_row === r;
            return (
              <div
                key={`${r}-${c}`}
                className={[
                  'viz__rotate-cell',
                  swapped  ? 'viz__rotate-cell--swap'    : '',
                  reversed ? 'viz__rotate-cell--reverse' : '',
                ].join(' ').trim()}
              >
                {cell}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
})()}


{cur.type === 'merge_intervals_timeline' && (
  <div className="viz__intervals">
    <div className="viz__intervals-phase">{cur.phase}</div>

    <div className="viz__interval-row">
      {cur.intervals?.map((it, i) => (
        <div
          key={i}
          className={[
            'viz__interval',
            cur.active    === i ? 'viz__interval--active'    : '',
            cur.candidate === i ? 'viz__interval--candidate' : '',
          ].join(' ').trim()}
        >
          [{it[0]}, {it[1]}]
        </div>
      ))}
    </div>

    {cur.merge_range && (
      <div className="viz__merged-preview">
        Merging → [{cur.merge_range[0]}, {cur.merge_range[1]}]
      </div>
    )}

    {cur.merged?.length > 0 && (
      <div className="viz__merged-list">
        {cur.merged.map((m, i) => (
          <div key={i} className="viz__merged-item">
            [{m[0]}, {m[1]}]
          </div>
        ))}
      </div>
    )}
  </div>
)}

{cur.type === 'gap_merge_transform' && (() => {
  const n = cur.left?.length ?? 0;
  return (
    <div className="viz__gap">
      <div className="viz__gap-phase">{cur.phase}</div>

      <div className="viz__gap-meta">
        Gap = <strong>{cur.gap}</strong>
        {cur.swapped && <span className="viz__gap-swap-badge">↔ Swapped</span>}
      </div>

      <div className="viz__gap-arrays">
        <div className="viz__gap-side">
          {cur.left?.map((v, i) => {
            const vIdx = i;
            const isCompare = cur.compare && (cur.compare[0] === vIdx || cur.compare[1] === vIdx);
            return (
              <div
                key={i}
                className={['viz__gap-cell', isCompare ? 'viz__gap-cell--compare' : ''].join(' ').trim()}
              >
                {v}
              </div>
            );
          })}
        </div>

        <div className="viz__gap-divider">|</div>

        <div className="viz__gap-side">
          {cur.right?.map((v, i) => {
            const vIdx = n + i;
            const isCompare = cur.compare && (cur.compare[0] === vIdx || cur.compare[1] === vIdx);
            return (
              <div
                key={i}
                className={['viz__gap-cell', isCompare ? 'viz__gap-cell--compare' : ''].join(' ').trim()}
              >
                {v}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
})()}



{/* ════════════════════════════
    kadane_transform — #-4 Kadane's Algorithm
    ════════════════════════════ */}
{cur.type === 'kadane_transform' && (
  <div className="viz__kadane">
    <div className="viz__kadane-phase">{cur.phase}</div>

    <div className="viz__kadane-row">
      {cur.array?.map((num, idx) => {
        const focused = cur.focus?.includes(idx);
        return (
          <div
            key={idx}
            className={['viz__kadane-cell', focused ? 'viz__kadane-cell--focus' : ''].join(' ').trim()}
          >
            {num}
          </div>
        );
      })}
    </div>

    <div className="viz__kadane-vars">
      <div>curr = <strong>{cur.curr}</strong></div>
      <div>best = <strong>{cur.best}</strong></div>
    </div>
  </div>
)}

{/* ════════════════════════════
    dnf_transform — #-5 Sort 0s 1s 2s (Dutch National Flag)
    ════════════════════════════ */}
{cur.type === 'dnf_transform' && (
  <div className="viz__dnf">
    <div className="viz__dnf-phase">{cur.phase}</div>

    <div className="viz__dnf-pointers">
      low = {cur.pointers?.low} | mid = {cur.pointers?.mid} | high = {cur.pointers?.high}
    </div>

    <div className="viz__dnf-row">
      {cur.array?.map((num, idx) => {
        const isLow  = cur.pointers?.low  === idx;
        const isMid  = cur.pointers?.mid  === idx;
        const isHigh = cur.pointers?.high === idx;
        return (
          <div
            key={idx}
            className={[
              'viz__dnf-cell',
              isLow  ? 'viz__dnf-cell--low'  : '',
              isMid  ? 'viz__dnf-cell--mid'  : '',
              isHigh ? 'viz__dnf-cell--high' : '',
            ].join(' ').trim()}
          >
            {num}
            {isLow  && <div className="viz__dnf-tag">LOW</div>}
            {isMid  && <div className="viz__dnf-tag">MID</div>}
            {isHigh && <div className="viz__dnf-tag">HIGH</div>}
          </div>
        );
      })}
    </div>
  </div>
)}

{/* ════════════════════════════
    stock_transform — #-6 Stock Buy and Sell
    ════════════════════════════ */}
{cur.type === 'stock_transform' && (
  <div className="viz__stock">
    <div className="viz__stock-phase">{cur.phase}</div>

    <div className="viz__stock-action">
      {cur.action === 'BUY'  && '📉 Buying Opportunity'}
      {cur.action === 'SELL' && '📈 Selling Opportunity'}
      {cur.action === 'SKIP' && '⏭ No Better Profit'}
      {cur.action === 'INIT' && '🚀 Starting Scan'}
      {cur.action === 'DONE' && '🏁 Final Result'}
    </div>

    <div className="viz__stock-row">
      {cur.array?.map((price, idx) => {
        const isCurrent = cur.index  === idx;
        const isBuy     = cur.buy    === idx;
        const isSell    = cur.sell   === idx;
        const isFocus   = cur.focus?.includes(idx);
        return (
          <div
            key={idx}
            className={[
              'viz__stock-cell',
              isCurrent ? 'viz__stock-cell--active' : '',
              isBuy     ? 'viz__stock-cell--buy'    : '',
              isSell    ? 'viz__stock-cell--sell'   : '',
              isFocus   ? 'viz__stock-cell--focus'  : '',
            ].join(' ').trim()}
          >
            {price}
            {isBuy  && <div className="viz__stock-tag">BUY</div>}
            {isSell && <div className="viz__stock-tag">SELL</div>}
          </div>
        );
      })}
    </div>

    <div className="viz__stock-vars">
      <div>minPrice = <strong>{cur.minPrice}</strong></div>
      <div>maxProfit = <strong>{cur.maxProfit}</strong></div>
    </div>
  </div>
)}

{/* ════════════════════════════════════════════════════
    ADD NEW VISUALIZERS BELOW THIS LINE
    ════════════════════════════════════════════════════ */}


{/* ════════════════════════════
    floyd_cycle_duplicate — #-10 Find the Duplicate Number
    ════════════════════════════ */}
{cur.type === 'floyd_cycle_duplicate' && (() => {
  const phaseColors = {
    init:   '',
    phase1: 'viz__floyd-phase1',
    meet:   'viz__floyd-meet',
    phase2: 'viz__floyd-phase2',
    found:  'viz__floyd-found',
  };
  return (
    <div className="viz__floyd">
      <div className="viz__floyd-phase">{cur.phase}</div>
 
      {/* Array with index labels */}
      <div className="viz__floyd-array">
        {cur.array?.map((val, idx) => (
          <div key={idx} className="viz__floyd-col">
            <div className="viz__floyd-idx">{idx}</div>
            <div className={[
              'viz__floyd-cell',
              cur.slow === idx ? 'viz__floyd-cell--slow' : '',
              cur.fast === idx ? 'viz__floyd-cell--fast' : '',
              (cur.slow === idx && cur.fast === idx) ? 'viz__floyd-cell--meet' : '',
            ].join(' ').trim()}>
              {val}
            </div>
            {cur.slow === idx && <div className="viz__floyd-tag viz__floyd-tag--slow">🐢slow</div>}
            {cur.fast === idx && cur.slow !== idx && <div className="viz__floyd-tag viz__floyd-tag--fast">🐇fast</div>}
            {cur.slow === idx && cur.fast === idx && <div className="viz__floyd-tag viz__floyd-tag--meet">MEET</div>}
          </div>
        ))}
      </div>
 
      {/* Pointer state */}
      <div className="viz__floyd-state">
        <span>slow = <strong>{cur.slow}</strong></span>
        <span>fast = <strong>{cur.fast}</strong></span>
        <span className="viz__floyd-plabel">{cur.phase_label?.toUpperCase()}</span>
      </div>
    </div>
  );
})()}
 
{/* ════════════════════════════
    xor_repeat_missing — #-11 Repeating and Missing Number
    ════════════════════════════ */}
{cur.type === 'xor_repeat_missing' && (
  <div className="viz__xor">
    <div className="viz__xor-phase">{cur.phase}</div>
 
    <div className="viz__xor-array">
      {cur.array?.map((val, idx) => (
        <div key={idx} className={[
          'viz__xor-cell',
          val === cur.repeat  ? 'viz__xor-cell--repeat'  : '',
          val === cur.missing ? 'viz__xor-cell--missing'  : '',
        ].join(' ').trim()}>
          {val}
        </div>
      ))}
    </div>
 
    {cur.xor_val !== undefined && (
      <div className="viz__xor-meta">
        XOR result = <strong>{cur.xor_val}</strong>
        {cur.bit !== undefined && <span> | Separator bit = <strong>{cur.bit}</strong></span>}
      </div>
    )}
 
    {cur.bucket0 && (
      <div className="viz__xor-buckets">
        <div className="viz__xor-bucket">
          <div className="viz__xor-bucket-lbl">Bucket 0</div>
          <div className="viz__xor-bucket-vals">{cur.bucket0.join(', ')}</div>
        </div>
        <div className="viz__xor-bucket">
          <div className="viz__xor-bucket-lbl">Bucket 1</div>
          <div className="viz__xor-bucket-vals">{cur.bucket1?.join(', ')}</div>
        </div>
      </div>
    )}
 
    {cur.repeat !== undefined && (
      <div className="viz__xor-result">
        <span className="viz__xor-repeat-tag">Repeat = {cur.repeat}</span>
        <span className="viz__xor-missing-tag">Missing = {cur.missing}</span>
      </div>
    )}
  </div>
)}
 
{/* ════════════════════════════
    merge_inversion_count — #-12 Inversion of Array
    ════════════════════════════ */}
{cur.type === 'merge_inversion_count' && (
  <div className="viz__inversion">
    <div className="viz__inversion-phase">{cur.phase}</div>
 
    <div className="viz__inversion-count">
      Inversions so far = <strong>{cur.count}</strong>
    </div>
 
    {/* Main array */}
    <div className="viz__inversion-row">
      {cur.array?.map((val, idx) => (
        <div key={idx} className="viz__inversion-cell">{val}</div>
      ))}
    </div>
 
    {/* Halves during merge */}
    {cur.left_half?.length > 0 && (
      <div className="viz__inversion-halves">
        <div className="viz__inversion-half viz__inversion-half--left">
          <div className="viz__inversion-half-lbl">Left (sorted)</div>
          <div className="viz__inversion-half-row">
            {cur.left_half.map((v, i) => (
              <div key={i} className={[
                'viz__inversion-cell',
                cur.comparing?.[0] === i ? 'viz__inversion-cell--active' : '',
              ].join(' ').trim()}>{v}</div>
            ))}
          </div>
        </div>
        <div className="viz__inversion-half viz__inversion-half--right">
          <div className="viz__inversion-half-lbl">Right (sorted)</div>
          <div className="viz__inversion-half-row">
            {cur.right_half?.map((v, i) => (
              <div key={i} className={[
                'viz__inversion-cell',
                cur.comparing?.[1] === i ? 'viz__inversion-cell--active' : '',
              ].join(' ').trim()}>{v}</div>
            ))}
          </div>
        </div>
      </div>
    )}
  </div>
)}


{/* ═══════════════════════════════════════════════════
          TWO POINTERS COMMON  —  #2540
      ════════════════════════════════════════════════════ */}
      {cur.type === 'two_pointers_common' && cur.nums1 && cur.nums2 && (
        <div className="viz__frame">
          <div className="viz__frame-hd">parallel pointers scanning sorted values</div>
          
          <div className="viz__tpc-container">
            {/* Array 1 Row */}
            <div className="viz__tpc-row">
              <span className="viz__tpc-label">nums1</span>
              <div className="viz__tpc-cells">
                {cur.nums1.map((val, idx) => {
                  const isActive = idx === cur.p1;
                  const isPast = idx < cur.p1;
                  return (
                    <div key={idx} className="viz__tpc-cell-block">
                      <div className={`viz__tpc-cell ${isActive ? 'viz__tpc-cell--active-p1' : ''} ${isPast ? 'viz__tpc-cell--past' : ''}`}>
                        {val}
                      </div>
                      <div className="viz__tpc-index">[{idx}]</div>
                      {isActive && <div className="viz__tpc-ptr-tag viz__tpc-ptr-tag--p1">p1</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Middle Comparator Section */}
            <div className="viz__tpc-comparator-box">
              <div className="viz__tpc-comp-card">
                <span className="viz__tpc-comp-lbl">comparing</span>
                <div className="viz__tpc-comp-equation">
                  <span className="viz__tpc-comp-val viz__tpc-comp-val--p1">nums1[{cur.p1}] = {cur.nums1[cur.p1]}</span>
                  <span className={`viz__tpc-comp-symbol viz__tpc-comp-symbol--${cur.comparison}`}>
                    {cur.comparison === 'less' ? '<' : cur.comparison === 'greater' ? '>' : cur.comparison === 'equal' ? '==' : '···'}
                  </span>
                  <span className="viz__tpc-comp-val viz__tpc-comp-val--p2">nums2[{cur.p2}] = {cur.nums2[cur.p2]}</span>
                </div>
                {cur.comparison !== 'none' && (
                  <div className={`viz__tpc-badge viz__tpc-badge--${cur.comparison}`}>
                    {cur.comparison === 'less' && 'Advance p1 (catch up)'}
                    {cur.comparison === 'greater' && 'Advance p2 (catch up)'}
                    {cur.comparison === 'equal' && '⚡ Match Found! Early Exit'}
                  </div>
                )}
              </div>
            </div>

            {/* Array 2 Row */}
            <div className="viz__tpc-row">
              <span className="viz__tpc-label">nums2</span>
              <div className="viz__tpc-cells">
                {cur.nums2.map((val, idx) => {
                  const isActive = idx === cur.p2;
                  const isPast = idx < cur.p2;
                  return (
                    <div key={idx} className="viz__tpc-cell-block">
                      {isActive && <div className="viz__tpc-ptr-tag viz__tpc-ptr-tag--p2">p2</div>}
                      <div className={`viz__tpc-cell ${isActive ? 'viz__tpc-cell--active-p2' : ''} ${isPast ? 'viz__tpc-cell--past' : ''}`}>
                        {val}
                      </div>
                      <div className="viz__tpc-index">[{idx}]</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

{/* ═══════════════════════════════════════════════════
          PREFIX COMMON ARRAY  —  #2657
      ════════════════════════════════════════════════════ */}
      {cur.type === 'prefix_common_scan' && cur.A && cur.B && (
        <div className="viz__frame">
          <div className="viz__frame-hd">tracking unique intersections per prefix index</div>
          
          <div className="viz__pcs-container">
            {/* Array A Row */}
            <div className="viz__pcs-row">
              <span className="viz__pcs-row-lbl">Array A</span>
              <div className="viz__pcs-cells">
                {cur.A.map((val, idx) => {
                  const isCurrent = idx === cur.i;
                  const isPast = idx < cur.i;
                  return (
                    <div key={idx} className={`viz__pcs-cell ${isCurrent ? 'viz__pcs-cell--active' : ''} ${isPast ? 'viz__pcs-cell--past' : ''}`}>
                      <span className="viz__pcs-cell-val">{val}</span>
                      <span className="viz__pcs-cell-idx">[{idx}]</span>
                      {isCurrent && <span className="viz__pcs-cell-ptr">i</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Array B Row */}
            <div className="viz__pcs-row">
              <span className="viz__pcs-row-lbl">Array B</span>
              <div className="viz__pcs-cells">
                {cur.B.map((val, idx) => {
                  const isCurrent = idx === cur.i;
                  const isPast = idx < cur.i;
                  return (
                    <div key={idx} className={`viz__pcs-cell ${isCurrent ? 'viz__pcs-cell--active' : ''} ${isPast ? 'viz__pcs-cell--past' : ''}`}>
                      <span className="viz__pcs-cell-val">{val}</span>
                      <span className="viz__pcs-cell-idx">[{idx}]</span>
                      {isCurrent && <span className="viz__pcs-cell-ptr">i</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Memory Sets State Section */}
            {cur.i >= 0 && (
              <div className="viz__pcs-state-grid">
                <div className="viz__pcs-set-card">
                  <span className="viz__pcs-set-hdr">seen_A (prefix A)</span>
                  <div className="viz__pcs-set-tokens">
                    {cur.seen_a.length === 0 ? <span className="viz__pcs-empty-set">∅</span> : cur.seen_a.map(val => (
                      <span key={val} className="viz__pcs-token viz__pcs-token--a">{val}</span>
                    ))}
                  </div>
                </div>
                
                <div className="viz__pcs-count-card">
                  <span className="viz__pcs-count-hdr">prefix matches</span>
                  <div className="viz__pcs-count-val">{cur.count}</div>
                </div>

                <div className="viz__pcs-set-card">
                  <span className="viz__pcs-set-hdr">seen_B (prefix B)</span>
                  <div className="viz__pcs-set-tokens">
                    {cur.seen_b.length === 0 ? <span className="viz__pcs-empty-set">∅</span> : cur.seen_b.map(val => (
                      <span key={val} className="viz__pcs-token viz__pcs-token--b">{val}</span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Accumulating Target Array C */}
            <div className="viz__pcs-result-row">
              <span className="viz__pcs-row-lbl">Result Array C</span>
              <div className="viz__pcs-cells">
                {cur.A.map((_, idx) => {
                  const calculated = idx <= cur.i;
                  const val = cur.c_so_far[idx];
                  return (
                    <div key={idx} className={`viz__pcs-cell viz__pcs-cell--c ${calculated ? 'viz__pcs-cell--c-filled' : ''}`}>
                      <span className="viz__pcs-cell-val">{calculated ? val : '·'}</span>
                      <span className="viz__pcs-cell-idx">[{idx}]</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

{/* ═══════════════════════════════════════════════════
          CHECK SORTED AND ROTATED  —  #1752
      ════════════════════════════════════════════════════ */}
      {cur.type === 'sorted_rotated_check' && cur.nums && (
        <div className="viz__frame">
          <div className="viz__frame-hd">circularly checking adjacent elements for inversion drops</div>
          
          <div className="viz__src-container">
            {/* Circular Array Cells row */}
            <div className="viz__src-row">
              <span className="viz__src-label">Array Track</span>
              <div className="viz__src-cells">
                {cur.nums.map((val, idx) => {
                  const isLeft = idx === cur.checking[0];
                  const isRight = idx === cur.checking[1];
                  const isCurrent = idx === cur.i;
                  
                  let cellModifier = '';
                  if (isLeft) cellModifier = 'viz__src-cell--left';
                  if (isRight) cellModifier = 'viz__src-cell--right';

                  return (
                    <div key={idx} className="viz__src-cell-block">
                      <div className={`viz__src-cell ${cellModifier} ${idx < cur.i ? 'viz__src-cell--past' : ''}`}>
                        {val}
                      </div>
                      <div className="viz__src-index">[{idx}]</div>
                      {isCurrent && <div className="viz__src-ptr">i</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Central Evaluation Box */}
            {cur.i >= 0 && (
              <div className="viz__src-eval-box">
                <div className="viz__src-card">
                  <span className="viz__src-card-hdr">circular compare</span>
                  <div className="viz__src-equation">
                    <span className="viz__src-eq-val viz__src-eq-val--left">nums[{cur.checking[0]}] = {cur.nums[cur.checking[0]]}</span>
                    <span className={`viz__src-symbol viz__src-symbol--${cur.condition}`}>
                      {cur.condition === 'invalid' ? '>' : '≤'}
                    </span>
                    <span className="viz__src-eq-val viz__src-eq-val--right">nums[{cur.checking[1]}] = {cur.nums[cur.checking[1]]}</span>
                  </div>
                  
                  <div className={`viz__src-badge viz__src-badge--${cur.condition}`}>
                    {cur.condition === 'valid' && '✓ Order Valid (No Break)'}
                    {cur.condition === 'invalid' && '⚠️ Drop Breakpoint Logged!'}
                  </div>
                </div>

                {/* Score Counter Card */}
                <div className={`viz__src-counter ${cur.breaks > 0 ? 'viz__src-counter--has-breaks' : ''}`}>
                  <span className="viz__src-counter-hdr">Total Breaks</span>
                  <div className="viz__src-counter-val">{cur.breaks}</div>
                  <span className="viz__src-counter-rule">(Max Allowed: 1)</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

{/* ═══════════════════════════════════════════════════
          JUMP GAME V  —  #1340
      ═══════════════════════════════════════════════════ */}
      {cur.type === 'dag_jump_scan' && cur.arr && (
        <div className="viz__frame">
          <div className="viz__frame-hd">scanning paths left and right (d = {cur.d}) with height visibility barriers</div>
          
          <div className="viz__djs-container">
            {/* Array Bars Layout Visualizer */}
            <div className="viz__djs-bars-frame">
              {cur.arr.map((height, idx) => {
                const isFocused = idx === cur.i;
                const isValidTarget = cur.valid_jumps?.includes(idx);
                const isBlockedTarget = cur.blocked_jumps?.includes(idx);
                
                let barModifier = '';
                if (isFocused) barModifier = 'viz__djs-bar--focused';
                else if (isValidTarget) barModifier = 'viz__djs-bar--valid';
                else if (isBlockedTarget) barModifier = 'viz__djs-bar--blocked';

                return (
                  <div key={idx} className="viz__djs-bar-column">
                    {/* Height Value Floating Tag */}
                    <span className={`viz__djs-bar-val ${isFocused ? 'viz__djs-bar-val--focused' : ''}`}>{height}</span>
                    
                    {/* Vertical Bar Representation */}
                    <div 
                      className={`viz__djs-bar ${barModifier}`}
                      style={{ height: `${(height / 15) * 100}px` }}
                    >
                      {isFocused && <div className="viz__djs-bar-pulse" />}
                    </div>

                    {/* Index Label */}
                    <span className="viz__djs-bar-idx">[{idx}]</span>
                    {isFocused && <span className="viz__djs-bar-ptr">i</span>}
                  </div>
                );
              })}
            </div>

            {/* Dynamic Programming Memo State row */}
            <div className="viz__djs-memo-row">
              <span className="viz__djs-memo-lbl">dp array (max visits)</span>
              <div className="viz__djs-memo-cells">
                {cur.dp.map((memoVal, idx) => {
                  const isFocused = idx === cur.i;
                  return (
                    <div key={idx} className={`viz__djs-memo-cell ${isFocused ? 'viz__djs-memo-cell--active' : ''} ${memoVal !== -1 ? 'viz__djs-memo-cell--computed' : ''}`}>
                      {memoVal === -1 ? '·' : memoVal}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

{/* ═══════════════════════════════════════════════════
          JUMP GAME VII  —  #1871
      ═══════════════════════════════════════════════════ */}
      {cur.type === 'sliding_jump_window' && cur.s && (
        <div className="viz__frame">
          <div className="viz__frame-hd">sliding source window tracking valid jump paths [i - maxJump .. i - minJump]</div>
          
          <div className="viz__sjw-container">
            {/* Binary String Characters Row */}
            <div className="viz__sjw-row">
              <span className="viz__sjw-lbl">Binary String (s)</span>
              <div className="viz__sjw-cells">
                {cur.s.split('').map((char, idx) => {
                  const isCurrent = idx === cur.i;
                  const inWindow = idx >= cur.win_left && idx <= cur.win_right;
                  
                  let cellModifier = '';
                  if (isCurrent) cellModifier = 'viz__sjw-cell--current';
                  else if (inWindow) cellModifier = 'viz__sjw-cell--in-window';
                  else if (idx < cur.win_left) cellModifier = 'viz__sjw-cell--past';

                  return (
                    <div key={idx} className="viz__sjw-cell-block">
                      <div className={`viz__sjw-cell ${cellModifier} ${char === '1' ? 'viz__sjw-cell--one' : ''}`}>
                        {char}
                      </div>
                      <div className="viz__sjw-index">[{idx}]</div>
                      {isCurrent && <div className="viz__sjw-ptr-tag">i</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Middle Real-time Status Card Panels */}
            <div className="viz__sjw-dashboard">
              {/* Sliding Window Bounds Card */}
              <div className="viz__sjw-stat-card">
                <span className="viz__sjw-stat-hdr">Jump Window Range</span>
                <div className="viz__sjw-stat-range">
                  [{Math.max(0, cur.win_left)} .. {Math.max(0, cur.win_right)}]
                </div>
                <span className="viz__sjw-stat-sub">indices able to reach i</span>
              </div>

              {/* Active Counter Box */}
              <div className={`viz__sjw-counter-card ${cur.reachable_count > 0 ? 'viz__sjw-counter-card--active' : ''}`}>
                <span className="viz__sjw-stat-hdr">Reachable Sources Inside</span>
                <div className="viz__sjw-counter-val">{cur.reachable_count}</div>
                <span className="viz__sjw-stat-sub">
                  {cur.reachable_count > 0 ? '✓ Path available' : '✗ Blocked from behind'}
                </span>
              </div>
            </div>

            {/* DP Reachability Boolean row */}
            <div className="viz__sjw-row viz__sjw-row--dp">
              <span className="viz__sjw-lbl">dp reachability status (true/false)</span>
              <div className="viz__sjw-cells">
                {cur.dp.map((reachable, idx) => {
                  const isCurrent = idx === cur.i;
                  return (
                    <div key={idx} className={`viz__sjw-dp-cell ${isCurrent ? 'viz__sjw-dp-cell--active' : ''} ${reachable ? 'viz__sjw-dp-cell--true' : ''}`}>
                      {reachable ? 'T' : 'F'}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

{/* ═══════════════════════════════════════════════════
   LAND + WATER RIDES — #3633
   ════════════════════════════════════════════════════ */}
{cur.type === 'land_water_finish_scan' &&
  cur.landStartTime &&
  cur.waterStartTime && (
    <div className="viz__frame">

      <div className="viz__frame-hd">
        testing all land-water combinations (land → water vs water → land)
      </div>

      <div className="viz__lws-container">

        {/* LAND */}
        <div className="viz__lws-row">
          <span className="viz__lws-row-lbl">Land Rides</span>
          <div className="viz__lws-cells">
            {cur.landStartTime.map((val, idx) => {
              const active = idx === cur.i;
              return (
                <div key={idx}
                  className={`viz__lws-cell ${active ? 'viz__lws-cell--active' : ''}`}>
                  <span className="viz__lws-cell-val">
                    S:{val} D:{cur.landDuration[idx]}
                  </span>
                  <span className="viz__lws-cell-idx">L{idx}</span>

                  {active && <span className="viz__lws-ptr">i</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* WATER */}
        <div className="viz__lws-row">
          <span className="viz__lws-row-lbl">Water Rides</span>
          <div className="viz__lws-cells">
            {cur.waterStartTime.map((val, idx) => {
              const active = idx === cur.j;
              return (
                <div key={idx}
                  className={`viz__lws-cell ${active ? 'viz__lws-cell--active' : ''}`}>
                  <span className="viz__lws-cell-val">
                    S:{val} D:{cur.waterDuration[idx]}
                  </span>
                  <span className="viz__lws-cell-idx">W{idx}</span>

                  {active && <span className="viz__lws-ptr">j</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* STATE */}
        {cur.i >= 0 && cur.j >= 0 && (
          <div className="viz__lws-state">

            <div className="viz__lws-card">
              <div className="viz__lws-hdr">Land → Water</div>
              <div className="viz__lws-val">{cur.planA}</div>
            </div>

            <div className="viz__lws-card viz__lws-best">
              <div className="viz__lws-hdr">Best</div>
              <div className="viz__lws-val">{cur.best}</div>
            </div>

            <div className="viz__lws-card">
              <div className="viz__lws-hdr">Water → Land</div>
              <div className="viz__lws-val">{cur.planB}</div>
            </div>

          </div>
        )}

      </div>
    </div>
)}


{/* ═══════════════════════════════════════════════════
          COUNT SPECIAL CHARACTERS I  —  #3120
      ═══════════════════════════════════════════════════ */}
      {cur.type === 'special_chars_check' && cur.word && (
        <div className="viz__frame">
          <div className="viz__frame-hd">populating case-specific checklists and evaluating alphabet overlaps</div>
          
          <div className="viz__scc-container">
            {/* Input Word Characters Scanning Row */}
            <div className="viz__scc-row">
              <span className="viz__scc-lbl">Scanning Word Stream</span>
              <div className="viz__scc-cells">
                {cur.word.split('').map((char, idx) => {
                  const isCurrent = idx === cur.i;
                  const isProcessed = idx < cur.i;
                  return (
                    <div key={idx} className="viz__scc-cell-block">
                      <div className={`viz__scc-cell ${isCurrent ? 'viz__scc-cell--current' : ''} ${isProcessed ? 'viz__scc-cell--past' : ''}`}>
                        {char}
                      </div>
                      <div className="viz__scc-index">[{idx}]</div>
                      {isCurrent && <div className="viz__scc-ptr">▲</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Dynamic Memory Checklist Dashboards */}
            <div className="viz__scc-dashboard">
              {/* Lowercase List Panel */}
              <div className="viz__scc-card">
                <span className="viz__scc-card-hdr">lowercase checklist</span>
                <div className="viz__scc-tokens">
                  {Array.from(new Set(cur.word.toLowerCase())).sort().map(letter => {
                    const checked = cur.seen_lower?.includes(letter);
                    return (
                      <span key={letter} className={`viz__scc-token ${checked ? 'viz__scc-token--lower-on' : ''}`}>
                        {letter} {checked ? '✓' : '·'}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Center Counter Totals Card Panel */}
              <div className={`viz__scc-counter ${cur.count > 0 ? 'viz__scc-counter--active' : ''}`}>
                <span className="viz__scc-counter-hdr">Special Letters</span>
                <div className="viz__scc-counter-val">{cur.count}</div>
                <span className="viz__scc-counter-sub">checked in both</span>
              </div>

              {/* Uppercase List Panel */}
              <div className="viz__scc-card">
                <span className="viz__scc-card-hdr">uppercase checklist</span>
                <div className="viz__scc-tokens">
                  {Array.from(new Set(cur.word.toLowerCase())).sort().map(letter => {
                    const checked = cur.seen_upper?.includes(letter);
                    return (
                      <span key={letter} className={`viz__scc-token ${checked ? 'viz__scc-token--upper-on' : ''}`}>
                        {letter.toUpperCase()} {checked ? '✓' : '·'}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

{/* ═══════════════════════════════════════════════════
          COUNT SPECIAL CHARACTERS II  —  #3121
      ═══════════════════════════════════════════════════ */}
      {cur.type === 'special_chars_two_check' && cur.word && (
        <div className="viz__frame">
          <div className="viz__frame-hd">monitoring case-specific boundary indices and order invalidation flags live</div>
          
          <div className="viz__sct-container">
            {/* Input Character Boxes row */}
            <div className="viz__sct-row">
              <span className="viz__sct-lbl">String Stream Scan</span>
              <div className="viz__sct-cells">
                {cur.word.split('').map((char, idx) => {
                  const isCurrent = idx === cur.i;
                  const isProcessed = idx < cur.i;
                  return (
                    <div key={idx} className="viz__sct-cell-block">
                      <div className={`viz__sct-cell ${isCurrent ? 'viz__sct-cell--current' : ''} ${isProcessed ? 'viz__sct-cell--past' : ''}`}>
                        {char}
                      </div>
                      <div className="viz__sct-index">[{idx}]</div>
                      {isCurrent && <div className="viz__sct-ptr">▲</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Alphabet Positional Tracking Table Dashboard */}
            <div className="viz__sct-dashboard">
              <table className="viz__sct-table">
                <thead>
                  <tr>
                    <th>Letter</th>
                    <th>Last Lower Index</th>
                    <th>First Upper Index</th>
                    <th>Status Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(new Set(cur.word.toLowerCase())).sort().map(letter => {
                    const lowerPos = cur.last_lower?.[letter] ?? -1;
                    const upperPos = cur.first_upper?.[letter] ?? -1;
                    const isInvalid = cur.invalid?.includes(letter);
                    const isSpecial = lowerPos !== -1 && upperPos !== -1 && !isInvalid;

                    return (
                      <tr key={letter} className={isInvalid ? 'viz__sct-tr--invalid' : isSpecial ? 'viz__sct-tr--special' : ''}>
                        <td className="viz__sct-table-letter">{letter.toUpperCase()} / {letter}</td>
                        <td className="viz__sct-table-val">{lowerPos === -1 ? '·' : lowerPos}</td>
                        <td className="viz__sct-table-val">{upperPos === -1 ? '·' : upperPos}</td>
                        <td>
                          {isInvalid && <span className="viz__sct-badge viz__sct-badge--invalid">✕ Invalid Order</span>}
                          {isSpecial && <span className="viz__sct-badge viz__sct-badge--special">✓ Special Pair</span>}
                          {!isInvalid && !isSpecial && <span className="viz__sct-badge viz__sct-badge--pending">· Incomplete</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Total Score Panel counter */}
              <div className={`viz__sct-counter ${cur.count > 0 ? 'viz__sct-counter--active' : ''}`}>
                <span className="viz__sct-counter-hdr">Valid Special Letters</span>
                <div className="viz__sct-counter-val">{cur.count}</div>
                <span className="viz__sct-counter-sub">order matching pairs</span>
              </div>
            </div>
          </div>
        </div>
      )}

{/* ═══════════════════════════════════════════════════
          MINIMUM ELEMENT DIGIT SUM  —  #3300
      ═══════════════════════════════════════════════════ */}
      {cur.type === 'digit_sum_min_check' && cur.nums && (
        <div className="viz__frame">
          <div className="viz__frame-hd">calculating digit sums element-by-element and reducing to minimum value</div>
          
          <div className="viz__dsm-container">
            {/* Input Array Elements Tracking row */}
            <div className="viz__dsm-row">
              <span className="viz__dsm-lbl">Input Numbers Array</span>
              <div className="viz__dsm-cells">
                {cur.nums.map((val, idx) => {
                  const isCurrent = idx === cur.i;
                  const isProcessed = idx < cur.i;
                  return (
                    <div key={idx} className="viz__dsm-cell-block">
                      <div className={`viz__dsm-cell ${isCurrent ? 'viz__dsm-cell--current' : ''} ${isProcessed ? 'viz__dsm-cell--past' : ''}`}>
                        {val}
                      </div>
                      <div className="viz__dsm-index">[{idx}]</div>
                      {isCurrent && <div className="viz__dsm-ptr">▲</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Live Transformation Dashboard Section */}
            <div className="viz__dsm-dashboard">
              {/* Left Side Active Computation Block */}
              <div className="viz__dsm-card">
                <span className="viz__dsm-card-hdr">active calculation</span>
                {cur.i >= 0 && cur.i < cur.nums.length ? (
                  <div className="viz__dsm-math-box">
                    <div className="viz__dsm-math-item">
                      <span className="viz__dsm-math-lbl">element</span>
                      <span className="viz__dsm-math-val viz__dsm-math-val--num">{cur.active_num}</span>
                    </div>
                    <div className="viz__dsm-math-sep">➔</div>
                    <div className="viz__dsm-math-item">
                      <span className="viz__dsm-math-lbl">digit splits</span>
                      <span className="viz__dsm-math-val viz__dsm-math-val--split">{cur.digits_math || '·'}</span>
                    </div>
                    <div className="viz__dsm-math-sep">=</div>
                    <div className="viz__dsm-math-item">
                      <span className="viz__dsm-math-lbl">sum</span>
                      <span className="viz__dsm-math-val viz__dsm-math-val--sum">{cur.current_sum}</span>
                    </div>
                  </div>
                ) : (
                  <div className="viz__dsm-idle-text">Waiting to select item...</div>
                )}
              </div>

              {/* Right Side Global Score Counter Panel */}
              <div className={`viz__dsm-counter ${cur.min_val !== 'inf' && cur.min_val !== '∞' ? 'viz__dsm-counter--active' : ''}`}>
                <span className="viz__dsm-counter-hdr">Global Min Sum</span>
                <div className="viz__dsm-counter-val">{cur.min_val}</div>
                <span className="viz__dsm-counter-sub">smallest so far</span>
              </div>
            </div>
          </div>
        </div>
      )}


{/* ═══════════════════════════════════════════════════
          BLOCK PLACEMENT QUERIES  —  #3161
      ═══════════════════════════════════════════════════ */}
      {cur.type === 'block_placement_scan' && cur.queries && (
        <div className="viz__bpq-container">
          <div className="viz__frame-hd">visualizing geometric obstacle splitting and offline reverse max-gap checks</div>

          {/* Context Parameters Summary Header */}
          <div className="viz__bpq-input-context-box">
            <div className="viz__bpq-context-item">
              <span className="viz__bpq-context-lbl">Walkthrough Target Example Input:</span>
              <code className="viz__bpq-context-code">queries = {cur.example_input || '[]'}</code>
            </div>
          </div>

          {/* Top Level Action Bar */}
          <div className="viz__bpq-action-bar">
            <span className="viz__bpq-action-lbl">Active Step Operation:</span>
            <span className={`viz__bpq-action-val ${cur.active_query.includes('Build') ? 'viz__bpq-action-val--build' : cur.active_query.includes('Check') ? 'viz__bpq-action-val--test' : ''}`}>
              {cur.active_query}
            </span>
          </div>

          {/* Geometric 1D Number Line Segment Canvas */}
          <div className="viz__bpq-canvas-card">
            <span className="viz__bpq-canvas-hdr">1D Coordinate Viewport</span>
            <div className="viz__bpq-line-axis">
              {/* Ground Track Axis */}
              <div className="viz__bpq-axis-line" />

              {/* Dynamic Coordinate Markers */}
              {Array.from({ length: 11 }).map((_, val) => {
                const isObstacle = cur.obstacles?.includes(val);
                return (
                  <div key={val} className="viz__bpq-tick-node" style={{ left: `${(val / 10) * 100}%` }}>
                    <div className={`viz__bpq-tick-pin ${isObstacle ? 'viz__bpq-tick-pin--obstacle' : ''}`}>
                      {isObstacle && <span className="viz__bpq-obs-flag">🛑</span>}
                    </div>
                    <span className="viz__bpq-tick-lbl">{val}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dashboard Information Grid Panel */}
          <div className="viz__bpq-dashboard">
            {/* Left Side Active Obstacle Set Card */}
            <div className="viz__bpq-card">
              <span className="viz__bpq-card-hdr">tracked obstacles registry</span>
              <div className="viz__bpq-set-box">
                {cur.obstacles?.map((obs, index) => (
                  <span key={index} className="viz__bpq-obs-chip">
                    coord {obs}
                  </span>
                ))}
              </div>
            </div>

            {/* Center Max Gap Tree Value Scorecard */}
            <div className="viz__bpq-card viz__bpq-card--center">
              <span className="viz__bpq-card-hdr">max gap found</span>
              <div className="viz__bpq-stat-val">{cur.max_gap_found || '0'}</div>
            </div>

            {/* Right Side Query Results Verdict Badge */}
            <div className={`viz__bpq-verdict-card viz__bpq-verdict-card--${cur.verdict}`}>
              <span className="viz__bpq-card-hdr">step verdict</span>
              <div className="viz__bpq-verdict-val">
                {cur.verdict === 'true' ? '✓ Fits' : cur.verdict === 'false' ? '✕ Blocked' : '· Wait'}
              </div>
            </div>
          </div>

          {/* Live Outcome Vector Tracker Card */}
          <div className="viz__bpq-results-vector-box">
            <span className="viz__bpq-vector-lbl">Results Vector Accumulation (Chronological Outcome):</span>
            <div className="viz__bpq-vector-row">
              <code className="viz__bpq-vector-code">{cur.results_so_far || '[]'}</code>
              {cur.done && <span className="viz__bpq-vector-final-tag">➔ Final Outcome reached!</span>}
            </div>
          </div>
        </div>
      )}

{/* ═══════════════════════════════════════════════════
          DESTROYING ASTEROIDS  —  #2126
      ═══════════════════════════════════════════════════ */}
      {cur.type === 'asteroid_conquer_scan' && cur.asteroids && (
        <div className="viz__ast-container">
          <div className="viz__frame-hd">visualizing greedy absorption of sorted asteroid weights</div>

          {/* Context Input Display Panel Row */}
          <div className="viz__ast-input-context-box">
            <div className="viz__ast-context-item">
              <span className="viz__ast-context-lbl">Walkthrough Array Sample Context:</span>
              <code className="viz__ast-context-code">{cur.example_input}</code>
            </div>
          </div>

          {/* Sorted Asteroids Train Tracker Element Row */}
          <div className="viz__ast-row">
            <span className="viz__ast-lbl">Sorted Space Rock Sequence</span>
            <div className="viz__ast-cells">
              {cur.asteroids.map((weight, idx) => {
                const isCurrent = idx === cur.i;
                const isProcessed = idx < cur.i;
                return (
                  <div key={idx} className="viz__ast-cell-block">
                    <div className={`viz__ast-cell ${isCurrent ? 'viz__ast-cell--current' : ''} ${isProcessed ? 'viz__ast-cell--past' : ''}`}>
                      {weight}
                    </div>
                    <div className="viz__ast-index">[{idx}]</div>
                    {isCurrent && <div className="viz__ast-ptr">☄️</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dynamic Information Dashboard Grid */}
          <div className="viz__ast-dashboard">
            {/* Left Side Active Computation Matrix Card */}
            <div className="viz__ast-card">
              <span className="viz__ast-card-hdr">active calculation</span>
              {cur.i >= 0 && cur.i < cur.asteroids.length ? (
                <div className="viz__ast-math-box">
                  <div className="viz__ast-math-item">
                    <span className="viz__ast-math-lbl">planet mass</span>
                    <span className="viz__ast-math-val viz__ast-math-val--mass">{cur.planet_mass - cur.active_rock}</span>
                  </div>
                  <span className="viz__ast-math-sep">+</span>
                  <div className="viz__ast-math-item">
                    <span className="viz__ast-math-lbl">rock weight</span>
                    <span className="viz__ast-math-val viz__ast-math-val--rock">{cur.active_rock}</span>
                  </div>
                  <span className="viz__ast-math-sep">=</span>
                  <div className="viz__ast-math-item">
                    <span className="viz__ast-math-lbl">new mass</span>
                    <span className="viz__ast-math-val viz__ast-math-val--sum">{cur.planet_mass}</span>
                  </div>
                </div>
              ) : (
                <div className="viz__ast-idle-text">
                  {cur.i === -1 ? 'Preparing space sequence walk...' : 'Absorbed all incoming targets!'}
                </div>
              )}
            </div>

            {/* Right Side Live Score Counter Panel */}
            <div className={`viz__ast-counter ${cur.verdict === 'valid' || cur.verdict === 'complete' ? 'viz__ast-counter--active' : cur.verdict === 'invalid' ? 'viz__ast-counter--failed' : ''}`}>
              <span className="viz__ast-counter-hdr">Current Planet Mass</span>
              <div className="viz__ast-counter-val">{cur.planet_mass}</div>
              <span className="viz__ast-counter-sub">
                {cur.verdict === 'invalid' ? '💥 Planet Destroyed!' : cur.verdict === 'complete' ? '🏆 Victory!' : 'Surviving'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          SLIDING WINDOW  —  #1461
      ════════════════════════════════════════════════════ */}
      {cur.chars && !cur.type && (
        <div className="viz__str">
          <div className="viz__str-label">string</div>
          <div className="viz__chars">
            {cur.chars.map((ch,ci) => {
              const inWin = cur.window && ci >= cur.window[0] && ci <= cur.window[1];
              const past  = cur.window && ci < cur.window[0];
              return (
                <div key={ci} className="viz__char-col">
                  <div className={`viz__char ${inWin?'viz__char--win':''}${past?' viz__char--past':''}`}>{ch}</div>
                  <div className={`viz__idx ${inWin?'viz__idx--on':''}`}>{ci}</div>
                </div>
              );
            })}
          </div>
          {cur.window && (
            <div className="viz__win-label">
              window [{cur.window[0]}..{cur.window[1]}] = <code>"{cur.chars.slice(cur.window[0],cur.window[1]+1).join('')}"</code>
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          FLIP WINDOW  —  #1888
          Doubled string + window + two live target rows with
          red (needs flip) / green (already matches) cells.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'flip_window' && cur.chars && (
        <div className="viz__frame">
          <div className="viz__frame-hd">sliding window on doubled string — flips shown per cell</div>

          <div className="viz__fw-section-lbl">doubled string  t = s + s</div>
          <div className="viz__fw-scroll">
            <div className="viz__fw-row">
              {cur.chars.map((ch, ci) => {
                const inWin = ci >= cur.window[0] && ci <= cur.window[1];
                const isPast = ci < cur.window[0];
                return (
                  <div key={ci} className="viz__fw-col">
                    <div className={[
                      'viz__fw-cell',
                      inWin  ? 'viz__fw-cell--win'  : '',
                      isPast ? 'viz__fw-cell--past' : '',
                    ].join(' ')}>{ch}</div>
                    <div className={`viz__fw-idx ${inWin ? 'viz__fw-idx--on' : ''}`}>{ci}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="viz__fw-targets">

            <div className="viz__fw-target-block">
              <div className="viz__fw-target-lbl viz__fw-target-lbl--0">
                target 0 &nbsp;·&nbsp; 010101…
                <span className="viz__fw-mis-badge viz__fw-mis-badge--0">{cur.mis0} flip{cur.mis0 !== 1 ? 's' : ''}</span>
              </div>
              <div className="viz__fw-scroll">
                <div className="viz__fw-row">
                  {cur.chars.map((ch, ci) => {
                    const inWin = ci >= cur.window[0] && ci <= cur.window[1];
                    if (!inWin) return (
                      <div key={ci} className="viz__fw-col">
                        <div className="viz__fw-cell viz__fw-cell--out">·</div>
                        <div className="viz__fw-idx">&nbsp;</div>
                      </div>
                    );
                    const expected = ci % 2 === 0 ? '0' : '1';
                    const match = ch === expected;
                    return (
                      <div key={ci} className="viz__fw-col">
                        <div className={`viz__fw-cell viz__fw-cell--target ${match ? 'viz__fw-cell--match' : 'viz__fw-cell--flip'}`}>
                          {match ? ch : expected}
                        </div>
                        {!match && <div className="viz__fw-flip-tag">flip</div>}
                        {match  && <div className="viz__fw-flip-tag viz__fw-flip-tag--ok">✓</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="viz__fw-target-block">
              <div className="viz__fw-target-lbl viz__fw-target-lbl--1">
                target 1 &nbsp;·&nbsp; 101010…
                <span className="viz__fw-mis-badge viz__fw-mis-badge--1">{cur.mis1} flip{cur.mis1 !== 1 ? 's' : ''}</span>
              </div>
              <div className="viz__fw-scroll">
                <div className="viz__fw-row">
                  {cur.chars.map((ch, ci) => {
                    const inWin = ci >= cur.window[0] && ci <= cur.window[1];
                    if (!inWin) return (
                      <div key={ci} className="viz__fw-col">
                        <div className="viz__fw-cell viz__fw-cell--out">·</div>
                        <div className="viz__fw-idx">&nbsp;</div>
                      </div>
                    );
                    const expected = ci % 2 === 0 ? '1' : '0';
                    const match = ch === expected;
                    return (
                      <div key={ci} className="viz__fw-col">
                        <div className={`viz__fw-cell viz__fw-cell--target ${match ? 'viz__fw-cell--match' : 'viz__fw-cell--flip'}`}>
                          {match ? ch : expected}
                        </div>
                        {!match && <div className="viz__fw-flip-tag">flip</div>}
                        {match  && <div className="viz__fw-flip-tag viz__fw-flip-tag--ok">✓</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>

          <div className="viz__fw-ans-row">
            <div className="viz__fw-ans-item">
              <span className="viz__fw-ans-lbl">mis0</span>
              <span className="viz__fw-ans-val viz__fw-ans-val--0">{cur.mis0}</span>
            </div>
            <div className="viz__fw-ans-item">
              <span className="viz__fw-ans-lbl">mis1</span>
              <span className="viz__fw-ans-val viz__fw-ans-val--1">{cur.mis1}</span>
            </div>
            <div className={`viz__fw-ans-item viz__fw-ans-item--best ${cur.ans_updated ? 'viz__fw-ans-item--updated' : ''}`}>
              <span className="viz__fw-ans-lbl">best so far</span>
              <span className="viz__fw-ans-val viz__fw-ans-val--best">{cur.ans}</span>
            </div>
          </div>

        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          DIAGONAL  —  #1980
          Grid of strings with diagonal cells highlighted.
          Answer builds char-by-char showing flip at each step.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'diagonal' && cur.nums && (
        <div className="viz__frame">
          <div className="viz__frame-hd">cantor's diagonal — read (i, i), flip the bit</div>

          {/* Grid */}
          <div className="viz__diag-grid">
            {cur.nums.map((row, ri) => (
              <div key={ri} className="viz__diag-row">
                <span className="viz__diag-row-lbl">nums[{ri}]</span>
                {row.split('').map((ch, ci) => {
                  const isDiag   = ri === ci;
                  const isActive = isDiag && ri === cur.step;
                  const isPast   = isDiag && ri < cur.step;
                  return (
                    <div key={ci} className={[
                      'viz__diag-cell',
                      isDiag   ? 'viz__diag-cell--diag'   : '',
                      isActive ? 'viz__diag-cell--active'  : '',
                      isPast   ? 'viz__diag-cell--past'    : '',
                    ].join(' ')}>
                      {ch}
                      {isActive && <span className="viz__diag-arrow">▼</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Answer building */}
          {cur.ans_so_far?.length > 0 && (
            <div className="viz__diag-ans-wrap">
              <span className="viz__diag-ans-lbl">answer so far</span>
              <div className="viz__diag-ans-chips">
                {cur.ans_so_far.map((ch, i) => (
                  <div key={i} className={`viz__diag-ans-chip ${i === cur.ans_so_far.length - 1 && !cur.done ? 'viz__diag-ans-chip--new' : ''}`}>
                    <span className="viz__diag-ans-pos">[{i}]</span>
                    <span className="viz__diag-ans-val">{ch}</span>
                    <span className="viz__diag-ans-from">← flip({cur.nums[i][i]})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          DP TABLE  —  #3129
          Side-by-side dp[i][j][0] and dp[i][j][1] grids.
          Active cell glows, filled cells show their value,
          recurrence string rendered below the grids.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'dp_table' && cur.table0 && (
        <div className="viz__frame">
          <div className="viz__frame-hd">
            3-D DP — dp[i][j][last] where i=zeros used, j=ones used, last=0 or 1
          </div>

          <div className="viz__dpt-grids">

            {/* — dp[i][j][0] grid — */}
            <div className="viz__dpt-half">
              <div className="viz__dpt-grid-lbl viz__dpt-grid-lbl--0">dp[i][j][<span>0</span>]  ends in 0</div>
              <div className="viz__dpt-grid">
                {/* col headers */}
                <div className="viz__dpt-corner"/>
                {cur.table0[0].map((_,j) => (
                  <div key={j} className="viz__dpt-hdr">j={j}</div>
                ))}
                {cur.table0.map((row, i) => (
                  <>
                    <div key={`lbl${i}`} className="viz__dpt-hdr">i={i}</div>
                    {row.map((val, j) => {
                      const isActive = cur.active_i === i && cur.active_j === j;
                      const isFilled = val > 0 || (cur.active_i !== null && (i < cur.active_i || (i === cur.active_i && j <= cur.active_j)));
                      return (
                        <div key={`${i}-${j}`} className={[
                          'viz__dpt-cell',
                          isActive  ? 'viz__dpt-cell--active' : '',
                          isFilled && !isActive ? 'viz__dpt-cell--filled' : '',
                        ].join(' ')}>
                          {val}
                        </div>
                      );
                    })}
                  </>
                ))}
              </div>
            </div>

            {/* — dp[i][j][1] grid — */}
            <div className="viz__dpt-half">
              <div className="viz__dpt-grid-lbl viz__dpt-grid-lbl--1">dp[i][j][<span>1</span>]  ends in 1</div>
              <div className="viz__dpt-grid">
                <div className="viz__dpt-corner"/>
                {cur.table1[0].map((_,j) => (
                  <div key={j} className="viz__dpt-hdr">j={j}</div>
                ))}
                {cur.table1.map((row, i) => (
                  <>
                    <div key={`lbl${i}`} className="viz__dpt-hdr">i={i}</div>
                    {row.map((val, j) => {
                      const isActive = cur.active_i === i && cur.active_j === j;
                      const isFilled = val > 0 || (cur.active_i !== null && (i < cur.active_i || (i === cur.active_i && j <= cur.active_j)));
                      return (
                        <div key={`${i}-${j}`} className={[
                          'viz__dpt-cell',
                          isActive  ? 'viz__dpt-cell--active' : '',
                          isFilled && !isActive ? 'viz__dpt-cell--filled' : '',
                        ].join(' ')}>
                          {val}
                        </div>
                      );
                    })}
                  </>
                ))}
              </div>
            </div>

          </div>

          {/* recurrence */}
          {cur.recurrence && (
            <div className="viz__dpt-rec">
              <div className="viz__dpt-rec-lbl">recurrence</div>
              <pre className="viz__dpt-rec-body">{cur.recurrence}</pre>
            </div>
          )}

          {/* answer row */}
          {cur.phase === 'answer' && (
            <div className="viz__dpt-ans">
              <span className="viz__dpt-ans-lbl">answer</span>
              <span className="viz__dpt-ans-val">
                dp[{cur.zero}][{cur.one}][0] + dp[{cur.zero}][{cur.one}][1]
                {' = '}
                {cur.table0[cur.zero][cur.one]} + {cur.table1[cur.zero][cur.one]}
                {' = '}
                <strong>{cur.table0[cur.zero][cur.one] + cur.table1[cur.zero][cur.one]}</strong>
              </span>
            </div>
          )}

        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          SET / CHIP  —  #1461
      ════════════════════════════════════════════════════ */}
      {cur.all_codes && (
        <div className="viz__set">
          <div className="viz__set-label">seen set  ({cur.seen?.length} / {cur.target})</div>
          <div className="viz__chips">
            {cur.all_codes.map(code => {
              const found = cur.seen?.includes(code);
              const isNew = cur.added === code;
              return (
                <div key={code} className={`viz__chip ${found?'viz__chip--found':'viz__chip--miss'} ${isNew?'viz__chip--new':''}`}>
                  {code}{found && <span className="viz__tick">✓</span>}
                </div>
              );
            })}
          </div>
          <div className="viz__bar-bg"><div className="viz__bar" style={{width:`${(cur.seen?.length/cur.target)*100}%`}}/></div>
          <div className="viz__bar-txt">{cur.seen?.length} / {cur.target} codes found</div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          BINARY SIMULATION  —  #1404
          Entire block (string + eq + state vars) inside
          viz__frame so it renders as one contained box.
          Equation row has overflow-x:auto scroll wrapper.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'binary_sim' && (
        <div className="viz__frame">

          <div className="viz__frame-hd">
            binary string — scan right → left, skip index 0
          </div>

          {/* bit cells */}
          <div className="viz__bsim-str">
            {cur.s && cur.s.split('').map((ch, pi) => {
              const isActive = cur.active_pos === pi;
              const isDone   = cur.active_pos !== null && pi > cur.active_pos;
              const isSkip   = pi === 0;
              return (
                <div key={pi} className="viz__bsim-col">
                  <div className={`viz__bsim-arrow ${isActive ? 'viz__bsim-arrow--on' : ''}`}>▼</div>
                  <div className={[
                    'viz__bsim-bit',
                    isActive  ? (cur.parity === 'odd' ? 'viz__bsim-bit--odd' : 'viz__bsim-bit--even') : '',
                    isDone    ? 'viz__bsim-bit--done' : '',
                    isSkip    ? 'viz__bsim-bit--skip' : '',
                    ch === '1' ? 'viz__bsim-bit--one' : '',
                  ].join(' ')}>{ch}</div>
                  <div className={`viz__bsim-idx ${isActive ? 'viz__bsim-idx--on' : ''}`}>[{pi}]</div>
                  {isSkip && <div className="viz__bsim-skip-tag">skip</div>}
                </div>
              );
            })}
          </div>

          {/* equation row — scroll wrapper prevents overflow */}
          {cur.active_pos !== null && cur.bit_val !== null && cur.bit_val !== undefined && (
            <div className="viz__bsim-eq-wrap">
              <div className="viz__bsim-eq">
                <div className="viz__bsim-eq-box viz__bsim-eq-box--neutral">
                  <span className="viz__bsim-eq-lbl">s[{cur.active_pos}]</span>
                  <span className="viz__bsim-eq-val">{cur.s ? cur.s[cur.active_pos] : '?'}</span>
                </div>
                <span className="viz__bsim-eq-op">+</span>
                <div className={`viz__bsim-eq-box ${cur.carry ? 'viz__bsim-eq-box--carry' : 'viz__bsim-eq-box--neutral'}`}>
                  <span className="viz__bsim-eq-lbl">carry</span>
                  <span className="viz__bsim-eq-val">{cur.carry}</span>
                </div>
                <span className="viz__bsim-eq-op">=</span>
                <div className={`viz__bsim-eq-box viz__bsim-eq-box--result ${cur.parity === 'odd' ? 'viz__bsim-eq-box--odd' : 'viz__bsim-eq-box--even'}`}>
                  <span className="viz__bsim-eq-lbl">bit</span>
                  <span className="viz__bsim-eq-val">{cur.bit_val}</span>
                </div>
                <div className={`viz__bsim-parity-badge viz__bsim-parity-badge--${cur.parity}`}>
                  {cur.parity === 'odd' ? '⚡ ODD' : '✓ EVEN'}
                </div>
                <div className="viz__bsim-ops">
                  {cur.parity === 'odd'
                    ? <><span className="viz__bsim-op viz__bsim-op--odd">add 1</span><span className="viz__bsim-op-arrow">→</span><span className="viz__bsim-op viz__bsim-op--odd">÷ 2</span><span className="viz__bsim-op-count">+2 steps</span></>
                    : <><span className="viz__bsim-op viz__bsim-op--even">÷ 2</span><span className="viz__bsim-op-count">+1 step</span></>
                  }
                </div>
              </div>
            </div>
          )}

          {/* state vars: steps + carry */}
          <div className="viz__bsim-state">
            <div className={`viz__bsim-var ${cur.steps > 0 ? 'viz__bsim-var--active' : ''}`}>
              <div className="viz__bsim-var-lbl">steps</div>
              <div className="viz__bsim-var-val viz__bsim-var-val--steps">{cur.steps}</div>
            </div>
            <div className={`viz__bsim-var ${cur.carry ? 'viz__bsim-var--carry' : ''}`}>
              <div className="viz__bsim-var-lbl">carry</div>
              <div className={`viz__bsim-var-val ${cur.carry ? 'viz__bsim-var-val--carry' : ''}`}>{cur.carry}</div>
            </div>
            {cur.steps_add && (
              <div className="viz__bsim-var viz__bsim-var--added">
                <div className="viz__bsim-var-lbl">this step</div>
                <div className="viz__bsim-var-val viz__bsim-var-val--added">+{cur.steps_add}</div>
              </div>
            )}
            {cur.done && cur.result_text && (
              <div className="viz__bsim-answer">
                <span className="viz__bsim-answer-lbl">Answer</span>
                <span className="viz__bsim-answer-val">{cur.result_text}</span>
              </div>
            )}
          </div>

        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          BIT SCAN  —  #868, #1356, #1784
          vars rendered by generic block below
      ════════════════════════════════════════════════════ */}
      {cur.bits && (
        <div className="viz__str">
          <div className="viz__str-label">bits of n={cur.vars?.n ?? ''} (LSB → MSB)</div>
          <div className="viz__chars">
            {cur.bits.map((b,bi) => {
              const on = cur.active === bi;
              return (
                <div key={bi} className="viz__char-col">
                  {on ? <div className="viz__arrow">▼</div> : <div className="viz__arrow viz__arrow--hide">▼</div>}
                  <div className={`viz__char viz__char--bit viz__char--bit-${b} ${on?'viz__char--bit-on':''}`}>{b}</div>
                  <div className={`viz__idx ${on?'viz__idx--on':''}`}>p{bi}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          PREFIX ARRAY  —  #2657
      ════════════════════════════════════════════════════ */}
      {cur.A && (
        <div className="viz__prefix">
          <div className="viz__prefix-row">
            <span className="viz__prefix-lbl">A</span>
            {cur.A.map((v,i)=>(
              <div key={i} className={`viz__prefix-cell ${cur.i===i?'viz__prefix-cell--on':cur.i>i?'viz__prefix-cell--past':''}`}>{v}</div>
            ))}
          </div>
          <div className="viz__prefix-row">
            <span className="viz__prefix-lbl">B</span>
            {cur.B.map((v,i)=>(
              <div key={i} className={`viz__prefix-cell ${cur.i===i?'viz__prefix-cell--on':cur.i>i?'viz__prefix-cell--past':''}`}>{v}</div>
            ))}
          </div>
          {cur.i >= 0 && (
            <div className="viz__prefix-sets">
              <div className="viz__prefix-set">seen_A = {'{'+cur.seen_a?.join(', ')+'}'}</div>
              <div className="viz__prefix-set">seen_B = {'{'+cur.seen_b?.join(', ')+'}'}</div>
              <div className="viz__prefix-count">common count = <strong>{cur.count}</strong></div>
            </div>
          )}
          {cur.c_so_far && (
            <div className="viz__prefix-c">
              C so far: [<strong>{cur.c_so_far?.join(', ')}</strong>]
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          BIT COUNT TABLE  —  #1356 (type=table)
      ════════════════════════════════════════════════════ */}
      {cur.type === 'table' && cur.rows && (
        <div className="viz__tbl-wrap">
          <table className="viz__tbl">
            <thead>
              <tr><th>value</th><th>binary</th><th>1-bits</th></tr>
            </thead>
            <tbody>
              {cur.rows.map((r, ri) => (
                <tr key={ri} className={`viz__tbl-row viz__tbl-row--bits-${r.bits}`}>
                  <td className="viz__tbl-val">{r.val}</td>
                  <td className="viz__tbl-bin">{r.bin}</td>
                  <td className="viz__tbl-bits">
                    {r.bin.split('').map((b, bi) => (
                      <span key={bi} className={`viz__tbl-bit ${b==='1'?'viz__tbl-bit--on':''}`}>{b}</span>
                    ))}
                    <span className="viz__tbl-cnt"> = {r.bits}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          TREE  —  #1022
          Tree + step label + vars all inside viz__frame
          so everything is in one bordered container.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'tree' && cur.tree_label && (
        <div className="viz__frame">

          <div className="viz__tree">
            <div className="viz__tree-row">
              <div className={`viz__tnode ${cur.node_highlight === 'root' ? 'viz__tnode--on' : ''}`}>1</div>
            </div>
            <div className="viz__tree-connectors">
              <div className="viz__tree-line viz__tree-line--left"/>
              <div className="viz__tree-line viz__tree-line--right"/>
            </div>
            <div className="viz__tree-row">
              <div className={`viz__tnode ${cur.node_highlight === 'L' ? 'viz__tnode--on' : ''}`}>0</div>
              <div className="viz__tree-spacer"/>
              <div className={`viz__tnode ${cur.node_highlight === 'R' ? 'viz__tnode--on' : ''}`}>1</div>
            </div>
            <div className="viz__tree-connectors viz__tree-connectors--wide">
              <div className="viz__tree-line viz__tree-line--left"/>
              <div className="viz__tree-line viz__tree-line--right"/>
              <div className="viz__tree-spacer-big"/>
              <div className="viz__tree-line viz__tree-line--left"/>
              <div className="viz__tree-line viz__tree-line--right"/>
            </div>
            <div className="viz__tree-row">
              <div className={`viz__tnode viz__tnode--leaf ${cur.node_highlight === 'LL' ? 'viz__tnode--on' : ''}`}>0</div>
              <div className="viz__tree-spacer"/>
              <div className={`viz__tnode viz__tnode--leaf ${cur.node_highlight === 'LR' ? 'viz__tnode--on' : ''}`}>1</div>
              <div className="viz__tree-spacer"/>
              <div className={`viz__tnode viz__tnode--leaf ${cur.node_highlight === 'RL' ? 'viz__tnode--on' : ''}`}>0</div>
              <div className="viz__tree-spacer"/>
              <div className={`viz__tnode viz__tnode--leaf ${cur.node_highlight === 'RR' ? 'viz__tnode--on' : ''}`}>1</div>
            </div>
          </div>

          <div className="viz__tree-step-label">{cur.tree_label}</div>

          {cur.vars && (
            <div className="viz__vars">
              {Object.entries(cur.vars).map(([k,v]) => {
                const changed = cur.changed?.includes(k);
                const isFinal = cur.final === k;
                return (
                  <div key={k} className={`viz__var ${changed?'viz__var--changed':''} ${isFinal?'viz__var--final':''}`}>
                    <div className="viz__var-lbl">{k}</div>
                    <div className="viz__var-val">{String(v)}</div>
                    {changed && <div className="viz__var-up">↑ updated</div>}
                    {isFinal && <div className="viz__var-up" style={{color:'var(--accent)'}}>✓ answer</div>}
                  </div>
                );
              })}
            </div>
          )}

        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          BIT XOR  —  #1009
          Three rows: n bits (original), mask bits (all 1s),
          result bits (flipped so far). Active column glows,
          already-flipped result cells stay teal.
          Vars panel shows k / mask / result below.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'bit_xor' && cur.n_bits && (
        <div className="viz__frame">
          <div className="viz__frame-hd">XOR each bit of n with mask bit — 0→1, 1→0</div>

          <div className="viz__bx-table">

            {/* Row labels + col headers */}
            <div className="viz__bx-row">
              <span className="viz__bx-row-lbl">n</span>
              {cur.n_bits.map((b, ci) => (
                <div key={ci} className={[
                  'viz__bx-cell',
                  'viz__bx-cell--n',
                  b === 1 ? 'viz__bx-cell--one' : 'viz__bx-cell--zero',
                  cur.active === ci ? 'viz__bx-cell--active' : '',
                  cur.active > ci   ? 'viz__bx-cell--past'   : '',
                ].join(' ')}>{b}</div>
              ))}
              <span className="viz__bx-row-val">{cur.vars?.n}</span>
            </div>

            {/* XOR operator row */}
            <div className="viz__bx-row">
              <span className="viz__bx-row-lbl viz__bx-row-lbl--op">⊕</span>
              {cur.mask_bits.map((b, ci) => (
                <div key={ci} className={[
                  'viz__bx-cell',
                  'viz__bx-cell--mask',
                  cur.active === ci ? 'viz__bx-cell--active' : '',
                ].join(' ')}>{b}</div>
              ))}
              <span className="viz__bx-row-val">{cur.vars?.mask ?? '?'}</span>
            </div>

            {/* Divider */}
            <div className="viz__bx-divider">
              <span className="viz__bx-row-lbl"/>
              {cur.n_bits.map((_, ci) => (
                <div key={ci} className="viz__bx-sep"/>
              ))}
            </div>

            {/* Result row — cells fill in as steps progress */}
            <div className="viz__bx-row">
              <span className="viz__bx-row-lbl viz__bx-row-lbl--res">=</span>
              {cur.result_bits.map((b, ci) => {
                const isDone   = cur.active >= ci;
                const isActive = cur.active === ci;
                const isPending = cur.active < ci;
                return (
                  <div key={ci} className={[
                    'viz__bx-cell',
                    'viz__bx-cell--res',
                    isDone && !isActive ? 'viz__bx-cell--res-done' : '',
                    isActive            ? 'viz__bx-cell--res-new'  : '',
                    isPending           ? 'viz__bx-cell--res-pending' : '',
                  ].join(' ')}>
                    {isDone ? b : '·'}
                  </div>
                );
              })}
              <span className="viz__bx-row-val viz__bx-row-val--res">
                {cur.active >= cur.n_bits.length - 1 ? cur.vars?.result : '...'}
              </span>
            </div>

          </div>

          {/* index labels */}
          <div className="viz__bx-idx-row">
            <span className="viz__bx-row-lbl"/>
            {cur.n_bits.map((_, ci) => (
              <div key={ci} className={`viz__bx-idx ${cur.active === ci ? 'viz__bx-idx--on' : ''}`}>
                [{ci}]
              </div>
            ))}
          </div>

          {/* vars: k, mask, result */}
          {cur.vars && (
            <div className="viz__vars">
              {Object.entries(cur.vars).map(([k, v]) => {
                const changed = cur.changed?.includes(k);
                const isFinal = cur.final === k;
                return (
                  <div key={k} className={`viz__var ${changed?'viz__var--changed':''} ${isFinal?'viz__var--final':''}`}>
                    <div className="viz__var-lbl">{k}</div>
                    <div className="viz__var-val">{String(v)}</div>
                    {changed && <div className="viz__var-up">↑ updated</div>}
                    {isFinal && <div className="viz__var-up" style={{color:'var(--accent)'}}>✓ answer</div>}
                  </div>
                );
              })}
            </div>
          )}

        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          WORKER BINARY SEARCH  —  #3296
          Binary search trace: shows lo/hi/mid bar + per-worker
          table with k computation and feasibility verdict.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'worker_bs' && (() => {
        const workers   = cur.worker_rows || [];
        const H         = cur.H;
        const totalK    = cur.total_k;
        const feasible  = cur.feasible;
        const lo        = cur.lo;
        const hi        = cur.hi;
        const mid       = cur.mid;
        const phase     = cur.phase;
        const isDone    = phase === 'complete';
        // binary search bar — map [1..hi] to 0..100%
        const loFrac  = hi > 1 ? ((lo - 1) / (hi - 1)) * 100 : 0;
        const hiFrac  = hi > 1 ? ((hi - 1) / (hi - 1)) * 100 : 100;
        const midFrac = (mid != null && hi > 1) ? ((mid - 1) / (hi - 1)) * 100 : null;
        return (
          <div className="wbs">
            {/* header row */}
            <div className="wbs__hd">
              <div className="wbs__pill">Binary Search on Answer</div>
              <div className="wbs__hd-vals">
                <span className="wbs__hv wbs__hv--lo">lo={lo}</span>
                <span className="wbs__hv-sep">·</span>
                {mid != null && <><span className="wbs__hv wbs__hv--mid">mid={mid}</span><span className="wbs__hv-sep">·</span></>}
                <span className="wbs__hv wbs__hv--hi">hi={hi}</span>
                {isDone && <span className="wbs__hv wbs__hv--ans">ans={lo}</span>}
              </div>
            </div>

            {/* search range bar */}
            <div className="wbs__bar-wrap">
              <div className="wbs__bar-track">
                <div className="wbs__bar-range" style={{ left: `${loFrac}%`, width: `${Math.max(hiFrac - loFrac, 0.5)}%` }} />
                {midFrac != null && <div className="wbs__bar-mid" style={{ left: `${midFrac}%` }} />}
              </div>
              <div className="wbs__bar-labels">
                <span style={{left:`${loFrac}%`}} className="wbs__bar-lbl">{lo}</span>
                {midFrac != null && <span style={{left:`${midFrac}%`}} className="wbs__bar-lbl wbs__bar-lbl--mid">{mid}</span>}
                <span style={{left:`${Math.min(hiFrac,99)}%`}} className="wbs__bar-lbl">{hi}</span>
              </div>
            </div>

            {/* worker table */}
            {workers.length > 0 && mid != null && (
              <div className="wbs__tbl-wrap">
                <div className="wbs__tbl-row wbs__tbl-hd">
                  <span>Worker</span>
                  <span>time (w)</span>
                  <span>⌊T/w⌋</span>
                  <span>max reduce k</span>
                  <span>Cost check</span>
                </div>
                {workers.map((row, i) => {
                  const work = Math.floor(mid / row.w);
                  return (
                    <div key={i} className="wbs__tbl-row">
                      <span className="wbs__worker-lbl">W{i}</span>
                      <span className="wbs__mono">{row.w}</span>
                      <span className="wbs__mono">{work}</span>
                      <span className="wbs__mono wbs__k">{row.k ?? '—'}</span>
                      <span className="wbs__cost">{row.cost_formula}</span>
                    </div>
                  );
                })}
                {/* totals row */}
                <div className="wbs__tbl-row wbs__tbl-total">
                  <span className="wbs__total-lbl" style={{gridColumn:'1/4'}}>Total capacity</span>
                  <span className="wbs__mono wbs__k">{totalK ?? '—'}</span>
                  <span className={`wbs__verdict ${feasible === true ? 'wbs__verdict--yes' : feasible === false ? 'wbs__verdict--no' : ''}`}>
                    {feasible === true  ? `${totalK} ≥ H=${H} ✓ feasible → hi=${mid}` :
                     feasible === false ? `${totalK} < H=${H} ✗ not feasible → lo=${mid+1}` : ''}
                  </span>
                </div>
              </div>
            )}

            {/* setup / done state when no mid */}
            {mid == null && (
              <div className="wbs__setup">
                <div className="wbs__setup-row">
                  <span className="wbs__setup-lbl">mountainHeight H</span>
                  <span className="wbs__setup-val">{H}</span>
                </div>
                <div className="wbs__setup-row">
                  <span className="wbs__setup-lbl">workerTimes</span>
                  <span className="wbs__setup-val">[{(cur.workerTimes||[]).join(', ')}]</span>
                </div>
                <div className="wbs__setup-row">
                  <span className="wbs__setup-lbl">search range lo…hi</span>
                  <span className="wbs__setup-val">{lo} … {hi}</span>
                </div>
              </div>
            )}

            {isDone && (
              <div className="wbs__done">
                <span className="wbs__done-icon">🏔</span>
                Answer = <strong>{lo}</strong> seconds — all workers together reduce exactly H={H} units.
              </div>
            )}
          </div>
        );
      })()}

      {/* ════════════════════════════════════════════════════
          GRAPH MST  —  #3600
          Left:  SVG graph — centered equilateral triangle
          Right: Algorithm trace — what the code is doing
                 this exact step, with classified edges list
      ════════════════════════════════════════════════════ */}
      {cur.type === 'graph_mst' && (() => {
        const allEdges = cur.edges_all || [];
        const treeMap  = new Map((cur.tree_edges||[]).map(e=>[e.id,e]));
        const activeId = cur.active_edge ?? -1;
        const npos     = cur.node_pos || [];

        // derive edge state
        const edgeState = e => {
          const te      = treeMap.get(e.id);
          const inTree  = !!te;
          const isActive= e.id === activeId;
          const upgraded= te?.upgraded || (isActive && cur.upgrading);
          if (isActive)            return 'active';
          if (inTree && upgraded)  return 'upgraded';
          if (inTree)              return 'tree';
          if (e.must)              return 'must';
          return 'dim';
        };

        // color map
        const EC = {
          active:   'var(--orange)',
          upgraded: '#f5c96a',
          tree:     'var(--accent)',
          must:     '#7eb8f7',
          dim:      'var(--bd2)',
        };

        return (
          <div className="gmst">

            {/* ══ HEADER ══ */}
            <div className="gmst__hd">
              <div className={`gmst__pill gmst__pill--${(cur.phase||'setup').replace(/\s+/g,'-')}`}>
                {cur.phase || 'setup'}
              </div>
              <div className="gmst__hd-meta">
                <span>T&nbsp;=&nbsp;<strong>{cur.target_T ?? '?'}</strong></span>
                <span className="gmst__hd-sep">·</span>
                <span>k&nbsp;=&nbsp;<strong>{cur.k}</strong></span>
                <span className="gmst__hd-sep">·</span>
                <span>added&nbsp;<strong>{(cur.tree_edges||[]).length}</strong>&nbsp;/&nbsp;{cur.n-1}</span>
              </div>
            </div>

            {/* ══ BODY: graph + trace ══ */}
            <div className="gmst__body">

              {/* ─ LEFT: SVG graph ─ */}
              <div className="gmst__graph-wrap">
                <svg className="gmst__svg" viewBox="0 0 260 195">

                  {/* edges drawn first */}
                  {allEdges.map(e => {
                    const p0 = npos[e.u], p1 = npos[e.v];
                    if (!p0||!p1) return null;
                    const st   = edgeState(e);
                    const col  = EC[st];
                    const te   = treeMap.get(e.id);
                    const upgraded = te?.upgraded || (e.id===activeId && cur.upgrading);
                    const lbl  = upgraded ? `${e.s}→${e.s*2}` : `${e.s}`;
                    const mx   = (p0.x+p1.x)/2, my = (p0.y+p1.y)/2;
                    const pw   = lbl.length * 6.5 + 14;
                    const sw   = st==='active' ? 5 : st==='upgraded' ? 4.5 : st==='tree' ? 4 : 2.5;
                    const op   = st==='dim' ? 0.35 : 1;
                    const dash = st==='must' ? '6 4' : 'none';
                    return (
                      <g key={e.id}>
                        <line
                          x1={p0.x} y1={p0.y} x2={p1.x} y2={p1.y}
                          stroke={col} strokeWidth={sw} opacity={op}
                          strokeDasharray={dash} strokeLinecap="round"
                          style={st==='active'?{filter:'drop-shadow(0 0 6px rgba(255,140,60,.7))'}
                              : st==='upgraded'?{filter:'drop-shadow(0 0 5px rgba(245,201,106,.5))'}
                              : {}}
                          className={st==='active' ? 'gmst__e-pulse' : ''}
                        />
                        {/* label pill */}
                        <rect x={mx-pw/2} y={my-11} width={pw} height={22} rx={5}
                          fill={st==='dim'?'var(--bg3)':st==='active'?'rgba(255,140,60,.15)':st==='upgraded'?'rgba(245,201,106,.15)':st==='tree'?'var(--accent-dim)':'rgba(126,184,247,.1)'}
                          stroke={col} strokeWidth={1} opacity={st==='dim'?.5:1}/>
                        <text x={mx} y={my+5} textAnchor="middle"
                          fill={st==='dim'?'var(--tx3)':col}
                          style={{fontSize:'11px',fontFamily:'var(--mono)',fontWeight:800}}>
                          {lbl}
                        </text>
                        {/* MUST tag */}
                        {e.must===1 && (
                          <text x={mx} y={my-15} textAnchor="middle"
                            fill="#7eb8f7"
                            style={{fontSize:'8px',fontFamily:'var(--mono)',fontWeight:800,letterSpacing:'.06em'}}>
                            MUST
                          </text>
                        )}
                      </g>
                    );
                  })}

                  {/* nodes drawn on top */}
                  {npos.map((p,i) => {
                    const inTree = (cur.tree_edges||[]).some(
                      te => allEdges.find(ae=>ae.id===te.id&&(ae.u===i||ae.v===i))
                    );
                    const isActive = allEdges.find(
                      ae=>ae.id===activeId&&(ae.u===i||ae.v===i)
                    );
                    return (
                      <g key={i}>
                        <circle cx={p.x} cy={p.y} r={22}
                          fill={inTree?'var(--accent-dim)':'var(--bg2)'}
                          stroke={isActive?'var(--orange)':inTree?'var(--accent-mid)':'var(--bd2)'}
                          strokeWidth={isActive?3:inTree?2.5:1.5}
                          style={isActive?{filter:'drop-shadow(0 0 6px rgba(255,140,60,.5))'}:{}}
                        />
                        <text x={p.x} y={p.y+6} textAnchor="middle"
                          fill={inTree?'var(--accent)':'var(--tx1)'}
                          style={{fontSize:'15px',fontFamily:'var(--mono)',fontWeight:900}}>
                          {i}
                        </text>
                      </g>
                    );
                  })}
                </svg>

                {/* legend row below graph */}
                <div className="gmst__legend">
                  {[['var(--accent)','none','In tree'],['#f5c96a','none','Upgraded ×2'],
                    ['var(--orange)','none','Processing'],['#7eb8f7','6 4','Must'],
                    ['var(--bd2)','none','Unused']
                  ].map(([col,dash,lbl])=>(
                    <div key={lbl} className="gmst__leg-item">
                      <svg width="26" height="10">
                        <line x1="0" y1="5" x2="26" y2="5"
                          stroke={col} strokeWidth="3"
                          strokeDasharray={dash==='none'?undefined:dash}
                          strokeLinecap="round"/>
                      </svg>
                      <span>{lbl}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* ─ RIGHT: algorithm trace panel ─ */}
              <div className="gmst__trace">

                {/* what is the code doing right now */}
                <div className="gmst__trace-section">
                  <div className="gmst__trace-hd">What the algorithm does</div>
                  <div className="gmst__trace-body">{cur.action}</div>
                </div>

                {/* edge classification for current T */}
                {cur.edge_classes && (
                  <div className="gmst__trace-section">
                    <div className="gmst__trace-hd">Edge classification (T = {cur.target_T})</div>
                    {cur.edge_classes.map((ec,i)=>(
                      <div key={i} className={`gmst__ec gmst__ec--${ec.tier}`}>
                        <span className="gmst__ec-edge">{ec.label}</span>
                        <span className="gmst__ec-rule">{ec.rule}</span>
                        <span className="gmst__ec-verdict">{ec.verdict}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* UF state */}
                <div className="gmst__trace-section">
                  <div className="gmst__trace-hd">Union-Find components</div>
                  <div className="gmst__uf-row">
                    {Array.from({length: cur.n}, (_,i) => {
                      const comp = cur.uf_components?.[i] ?? i;
                      return (
                        <div key={i} className={`gmst__uf-node gmst__uf-node--c${comp%6}`}>
                          {i}
                        </div>
                      );
                    })}
                  </div>
                  {cur.uf_note && <div className="gmst__uf-note">{cur.uf_note}</div>}
                </div>

              </div>
            </div>

          </div>
        );
      })()}








      {/* ═══════════════════════════════════════════════════
          JUMP GAME IV BFS  —  #1345
          Array cells with BFS levels. Same-value groups
          shown with matching accent rings. Jump arcs drawn
          as curved SVG lines connecting same-value cells.
          Visited cells teal, frontier blue, active orange.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'jump_bfs_iv' && (() => {
        const arr       = cur.arr      || [];
        const visited   = cur.visited  || [];
        const frontier  = cur.frontier || [];
        const active    = cur.active   ?? -1;
        const sameGroup = cur.same_group || [];  // indices sharing value with active
        const steps     = cur.steps    ?? 0;
        const found     = cur.found    ?? false;
        const phase     = cur.phase    || '';
        const n         = arr.length;

        // Unique value-group colors cycling through palette
        const groupColors = ['var(--orange)','var(--purple)','var(--gold)','#c89bff','var(--hard)','#7eb8f7'];
        const valueGroups = {};
        arr.forEach((v,i) => { if (!valueGroups[v]) valueGroups[v]=[]; valueGroups[v].push(i); });
        const groupIndex = {};
        let gi = 0;
        Object.entries(valueGroups).forEach(([v, idxs]) => {
          if (idxs.length > 1) { groupIndex[v] = gi % groupColors.length; gi++; }
        });

        const cellState = i => {
          if (i === n-1 && found)   return 'goal-reached';
          if (i === n-1)            return 'goal';
          if (i === active)         return 'active';
          if (frontier.includes(i)) return 'frontier';
          if (visited.includes(i))  return 'visited';
          return 'idle';
        };

        const stateStyle = s => ({
          'goal-reached':{ bg:'rgba(57,197,187,.2)',  bd:'var(--accent)',    color:'var(--accent)' },
          'goal':        { bg:'rgba(57,197,187,.08)', bd:'var(--accent-mid)',color:'var(--accent)' },
          'active':      { bg:'rgba(255,140,60,.15)', bd:'rgba(255,140,60,.5)',color:'var(--orange)'},
          'frontier':    { bg:'var(--blue-dim)',      bd:'rgba(126,184,247,.4)',color:'var(--blue)' },
          'visited':     { bg:'var(--accent-dim)',    bd:'var(--accent-mid)', color:'var(--accent)'},
          'idle':        { bg:'var(--bg4)',            bd:'var(--bd)',         color:'var(--tx3)'  },
        }[s]);

        return (
          <div className="jgiv">
            {/* Header */}
            <div className="jgiv__hd">
              <span className="jgiv__phase">{phase}</span>
              <span className="jgiv__steps">steps = {steps}</span>
              {found && <span className="jgiv__found">✓ reached last index!</span>}
            </div>

            {/* Array cells */}
            <div className="jgiv__arr-wrap">
              <div className="jgiv__arr" style={{gridTemplateColumns:`repeat(${Math.min(n,10)},1fr)`}}>
                {arr.map((v, i) => {
                  const s   = cellState(i);
                  const st  = stateStyle(s);
                  const gc  = groupIndex[v] !== undefined ? groupColors[groupIndex[v]] : null;
                  const inSame = sameGroup.includes(i) && i !== active;
                  return (
                    <div key={i}
                      className={`jgiv__cell jgiv__cell--${s} ${inSame ? 'jgiv__cell--same' : ''}`}
                      style={{
                        background: st.bg,
                        borderColor: inSame ? (gc || st.bd) : st.bd,
                        color: st.color,
                        boxShadow: inSame ? `0 0 0 3px ${gc || 'var(--orange)'}22` : undefined,
                      }}>
                      <div className="jgiv__cell-val">{v}</div>
                      <div className="jgiv__cell-idx">{i}</div>
                      {i === 0   && <div className="jgiv__cell-tag jgiv__cell-tag--start">S</div>}
                      {i === n-1 && <div className="jgiv__cell-tag jgiv__cell-tag--end">E</div>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Value groups display */}
            <div className="jgiv__groups">
              <span className="jgiv__groups-lbl">value groups:</span>
              {Object.entries(valueGroups)
                .filter(([,idxs]) => idxs.length > 1)
                .map(([v, idxs]) => (
                  <div key={v} className="jgiv__group-chip"
                    style={{
                      color: groupColors[groupIndex[v]],
                      borderColor: groupColors[groupIndex[v]] + '55',
                      background: groupColors[groupIndex[v]] + '11',
                    }}>
                    {v}: [{idxs.join(',')}]
                  </div>
                ))
              }
            </div>

            {/* Legend */}
            <div className="jgiv__legend">
              {[['visited','teal','visited'],['frontier','blue','BFS frontier'],
                ['active','orange','processing'],['goal','teal-bright','last index']
              ].map(([cls,c,lbl])=>(
                <div key={cls} className={`jgiv__leg jgiv__leg--${cls}`}>{lbl}</div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════
          JUMP GAME BFS  —  #1306
          Array cells displayed as a row. BFS frontier shown
          in orange, visited cells in teal, zero cells in
          green, out-of-bounds jumps shown as arrows that
          fade. Current step's jump arrows rendered above/below.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'jump_bfs' && (() => {
        const arr      = cur.arr     || [];
        const visited  = cur.visited || [];   // indices visited so far
        const frontier = cur.frontier|| [];   // current BFS queue indices
        const active   = cur.active  ?? -1;   // index being processed this step
        const jumpFrom = cur.jump_from ?? -1;
        const jumpTo   = cur.jump_to  || [];  // [left, right] indices (-1 if OOB)
        const found    = cur.found   ?? false;
        const phase    = cur.phase   || '';
        const n        = arr.length;

        const cellState = i => {
          if (arr[i] === 0)          return 'zero';
          if (i === active)          return 'active';
          if (frontier.includes(i))  return 'frontier';
          if (visited.includes(i))   return 'visited';
          return 'idle';
        };

        const stateStyle = s => ({
          zero:     {bg:'rgba(78,201,148,.15)',  bd:'rgba(78,201,148,.5)',  color:'var(--easy)'},
          active:   {bg:'rgba(255,140,60,.15)',  bd:'rgba(255,140,60,.5)',  color:'var(--orange)'},
          frontier: {bg:'rgba(126,184,247,.12)', bd:'rgba(126,184,247,.35)',color:'var(--blue)'},
          visited:  {bg:'var(--accent-dim)',      bd:'var(--accent-mid)',    color:'var(--accent)'},
          idle:     {bg:'var(--bg4)',             bd:'var(--bd)',            color:'var(--tx3)'},
        }[s]);

        return (
          <div className="jg">
            {/* Phase badge */}
            <div className="jg__hd">
              <span className="jg__phase">{phase}</span>
              {found && <span className="jg__found">✓ found 0!</span>}
            </div>

            {/* Array cells */}
            <div className="jg__arr-wrap">
              {/* Jump arrows (above) */}
              <div className="jg__arrows-above">
                {jumpTo.map((to, ti) => {
                  if (to === undefined || to === null || to < 0 || to >= n) return null;
                  const fromCenter = jumpFrom / n * 100 + (1/n*50);
                  const toCenter   = to / n * 100 + (1/n*50);
                  const isLeft = to < jumpFrom;
                  if (!isLeft) return null;  // left jumps go above
                  return (
                    <div key={ti} className="jg__arrow-line jg__arrow-line--above"
                      style={{
                        left: `${Math.min(fromCenter, toCenter)}%`,
                        width: `${Math.abs(fromCenter - toCenter)}%`,
                      }}>
                      <div className="jg__arrow-tip jg__arrow-tip--left">←</div>
                    </div>
                  );
                })}
              </div>

              {/* Cells */}
              <div className="jg__arr" style={{gridTemplateColumns:`repeat(${n},1fr)`}}>
                {arr.map((v, i) => {
                  const s = cellState(i);
                  const st = stateStyle(s);
                  const isZero = v === 0;
                  return (
                    <div key={i} className={`jg__cell jg__cell--${s}`}
                      style={{background:st.bg,borderColor:st.bd,color:st.color}}>
                      <div className="jg__cell-val">{v}</div>
                      <div className="jg__cell-idx">{i}</div>
                      {isZero && <div className="jg__zero-star">★</div>}
                    </div>
                  );
                })}
              </div>

              {/* Jump arrows (below) */}
              <div className="jg__arrows-below">
                {jumpTo.map((to, ti) => {
                  if (to === undefined || to === null || to < 0 || to >= n) return null;
                  const fromCenter = (jumpFrom + 0.5) / n * 100;
                  const toCenter   = (to + 0.5) / n * 100;
                  const isRight = to > jumpFrom;
                  if (!isRight) return null;
                  return (
                    <div key={ti} className="jg__arrow-line jg__arrow-line--below"
                      style={{
                        left: `${Math.min(fromCenter, toCenter)}%`,
                        width: `${Math.abs(fromCenter - toCenter)}%`,
                      }}>
                      <div className="jg__arrow-tip jg__arrow-tip--right">→</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Legend */}
            <div className="jg__legend">
              {[['visited','teal','visited (BFS done)'],
                ['frontier','blue','in queue (frontier)'],
                ['active','orange','processing now'],
                ['zero','green','target (value=0)']
              ].map(([cls,c,lbl])=>(
                <div key={cls} className={`jg__leg jg__leg--${cls}`}>{lbl}</div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════
          ROBOT GRID  —  #657
          2D coordinate plane showing robot path step by step.
          Current position highlighted, trail shown as dots,
          origin marked. Running x/y counters beside the grid.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'robot_grid' && (() => {
        const pos      = cur.pos   || [0, 0];   // [x, y] current
        const trail    = cur.trail || [];        // [[x,y],...] visited (incl current)
        const move     = cur.move  || '';        // current move char
        const x        = cur.x ?? 0;
        const y        = cur.y ?? 0;
        const counts   = cur.counts || {R:0,L:0,U:0,D:0};
        const isDone   = cur.done;
        const result   = cur.result;            // true/false if done

        // Grid display: -3 to +3 on each axis → 7×7
        const HALF = 3;
        const SIZE = HALF * 2 + 1;  // 7

        const dirArrow = {R:'→',L:'←',U:'↑',D:'↓'};

        return (
          <div className="rg">
            {/* Move badge */}
            <div className="rg__hd">
              {move && (
                <span className="rg__move-badge">
                  {dirArrow[move] || move} {move}
                </span>
              )}
              {isDone && (
                <span className={`rg__result ${result ? 'rg__result--true' : 'rg__result--false'}`}>
                  {result ? '✓ back at origin' : '✗ not at origin'}
                </span>
              )}
            </div>

            <div className="rg__body">
              {/* Grid */}
              <div className="rg__grid-wrap">
                <div className="rg__grid">
                  {Array.from({length: SIZE}, (_, ri) => {
                    const gy = HALF - ri;   // y-axis: top=+HALF, bottom=-HALF
                    return Array.from({length: SIZE}, (_, ci) => {
                      const gx = ci - HALF;
                      const isOrigin  = gx === 0 && gy === 0;
                      const isCurrent = gx === x && gy === y;
                      const isTrail   = trail.some(t => t[0]===gx && t[1]===gy) && !isCurrent;
                      return (
                        <div key={`${ri}-${ci}`}
                          className={[
                            'rg__cell',
                            isOrigin  ? 'rg__cell--origin'  : '',
                            isCurrent ? 'rg__cell--current' : '',
                            isTrail   ? 'rg__cell--trail'   : '',
                            gx === 0  ? 'rg__cell--axis-y'  : '',
                            gy === 0  ? 'rg__cell--axis-x'  : '',
                          ].join(' ')}>
                          {isCurrent ? '🤖' : isOrigin ? '⊙' : isTrail ? '·' : ''}
                        </div>
                      );
                    });
                  })}
                </div>
                {/* Axis labels */}
                <div className="rg__axis-labels">
                  <span className="rg__lbl-y">y</span>
                  <span className="rg__lbl-x">x</span>
                </div>
              </div>

              {/* Counters panel */}
              <div className="rg__counters">
                <div className="rg__pos-card">
                  <div className="rg__pos-lbl">position</div>
                  <div className="rg__pos-val">
                    ({x}, {y})
                  </div>
                </div>
                <div className="rg__count-grid">
                  {[['R','→','blue'],['L','←','blue'],['U','↑','green'],['D','↓','green']].map(([d,arrow,color])=>(
                    <div key={d} className={`rg__count rg__count--${color}`}>
                      <span className="rg__count-dir">{arrow}{d}</span>
                      <span className="rg__count-n">{counts[d] || 0}</span>
                    </div>
                  ))}
                </div>
                {isDone && (
                  <div className="rg__check">
                    <div className={`rg__check-row ${counts.L === counts.R ? 'rg__check--ok' : 'rg__check--no'}`}>
                      L={counts.L} {counts.L === counts.R ? '=' : '≠'} R={counts.R}
                    </div>
                    <div className={`rg__check-row ${counts.U === counts.D ? 'rg__check--ok' : 'rg__check--no'}`}>
                      U={counts.U} {counts.U === counts.D ? '=' : '≠'} D={counts.D}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════
          CIPHER MATRIX  —  #2075
          Shows the rows×cols grid with diagonal-fill
          pattern highlighted. Each diagonal (start col)
          is a different accent color. Cells read row-wise
          from encodedText; decoded by reading diagonals.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'cipher_matrix' && (() => {
        const grid      = cur.grid    || [];    // 2D array of {char, diag, active}
        const rows      = cur.rows    || 0;
        const cols      = cur.cols    || 0;
        const activeDiag = cur.active_diag ?? -1;
        const decoded   = cur.decoded || '';
        const phase     = cur.phase   || '';

        // Colors per diagonal index cycling through accent palette
        const diagColors = [
          'var(--accent)', 'var(--blue)', 'var(--orange)', 'var(--purple)',
          'var(--gold)',   'var(--easy)', '#c89bff',       'var(--hard)',
        ];
        const diagBgs = [
          'var(--accent-dim)', 'var(--blue-dim)', 'rgba(255,140,60,.1)',  'rgba(167,139,250,.1)',
          'rgba(245,201,106,.1)', 'rgba(78,201,148,.1)', 'rgba(200,150,255,.1)', 'rgba(240,113,120,.1)',
        ];
        const diagBds = [
          'var(--accent-mid)', 'rgba(126,184,247,.3)', 'rgba(255,140,60,.3)', 'rgba(167,139,250,.3)',
          'rgba(245,201,106,.3)', 'rgba(78,201,148,.3)', 'rgba(200,150,255,.3)', 'rgba(240,113,120,.3)',
        ];

        return (
          <div className="cm">
            {/* Phase badge */}
            <div className="cm__hd">
              <span className="cm__phase">{phase}</span>
              {decoded && (
                <div className="cm__decoded">
                  <span className="cm__decoded-lbl">decoded →</span>
                  <span className="cm__decoded-val">"{decoded}"</span>
                </div>
              )}
            </div>

            {/* Matrix grid */}
            <div className="cm__grid-wrap">
              <div className="cm__grid" style={{gridTemplateColumns: `repeat(${cols}, 1fr)`}}>
                {grid.map((row, ri) => row.map((cell, ci) => {
                  const isActive = cell.diag === activeDiag;
                  const isPast   = activeDiag >= 0 && cell.diag < activeDiag;
                  const isDiag   = cell.diag >= 0;
                  const color    = isDiag ? diagColors[cell.diag % diagColors.length] : 'var(--tx3)';
                  const bg       = isActive ? diagBgs[cell.diag % diagBgs.length]
                                 : isPast   ? 'rgba(255,255,255,.03)'
                                 : 'var(--bg4)';
                  const bd       = isActive ? diagBds[cell.diag % diagBds.length]
                                 : isPast   ? 'var(--bd)'
                                 : 'var(--bd)';
                  return (
                    <div key={`${ri}-${ci}`}
                      className={[
                        'cm__cell',
                        isActive ? 'cm__cell--active' : '',
                        isPast   ? 'cm__cell--past'   : '',
                        cell.char === ' ' ? 'cm__cell--space' : '',
                      ].join(' ')}
                      style={{ borderColor: bd, background: bg }}>
                      <span className="cm__cell-char" style={{color: isDiag ? color : 'var(--tx3)'}}>
                        {cell.char === ' ' ? '·' : cell.char}
                      </span>
                      {isDiag && (
                        <span className="cm__cell-idx">[{ri},{ci}]</span>
                      )}
                    </div>
                  );
                }))}
              </div>

              {/* Column index headers */}
              <div className="cm__col-hdrs" style={{gridTemplateColumns: `repeat(${cols}, 1fr)`}}>
                {Array.from({length: cols}, (_, i) => (
                  <div key={i} className="cm__col-hdr">c{i}</div>
                ))}
              </div>
            </div>

            {/* Diagonal legend */}
            {activeDiag >= 0 && (
              <div className="cm__legend">
                {Array.from({length: cols}, (_, d) => (
                  <div key={d}
                    className={`cm__leg-chip ${d === activeDiag ? 'cm__leg-chip--on' : d < activeDiag ? 'cm__leg-chip--done' : ''}`}
                    style={{
                      color: d <= activeDiag ? diagColors[d % diagColors.length] : 'var(--tx3)',
                      borderColor: d <= activeDiag ? diagBds[d % diagBds.length] : 'var(--bd)',
                      background: d === activeDiag ? diagBgs[d % diagBgs.length] : d < activeDiag ? 'rgba(255,255,255,.03)' : 'var(--bg3)',
                    }}>
                    d{d}{d < activeDiag ? ' ✓' : d === activeDiag ? ' ←' : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════
          ROBOT WALLS DP  —  #3661
          Number-line with robots, walls, and bullet ranges.
          Shows left/right attack ranges per robot, the dp
          state (subLeft, subRight), and the optimal choice.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'robot_walls' && (() => {
        const positions = cur.positions || [];  // [{pos, d, dir, leftRange, rightRange}]
        const walls     = cur.walls     || [];  // all wall positions
        const minX      = cur.minX ?? 0;
        const maxX      = cur.maxX ?? 20;
        const spanW     = maxX - minX || 1;
        const dpState   = cur.dp_state  || null; // {subL, subR, curL, curR}
        const phase     = cur.phase     || '';
        const hitWalls  = cur.hit_walls || [];   // walls destroyed so far

        const toFrac = x => ((x - minX) / spanW) * 100;

        return (
          <div className="rwd">
            {/* Phase badge */}
            <div className="rwd__hd">
              <span className="rwd__phase">{phase}</span>
              {cur.done && (
                <span className="rwd__done-pill">✓ {hitWalls.length} walls destroyed</span>
              )}
            </div>

            {/* Number line */}
            <div className="rwd__line-wrap">
              <div className="rwd__axis"/>

              {/* Range bars */}
              {positions.map((rb, ri) => (
                <div key={ri}>
                  {rb.leftRange && (
                    <div className="rwd__range rwd__range--left"
                      style={{
                        left:  `${toFrac(rb.leftRange[0])}%`,
                        width: `${toFrac(rb.leftRange[1]) - toFrac(rb.leftRange[0])}%`,
                        top: `${14 + ri * 10}px`,
                      }}/>
                  )}
                  {rb.rightRange && (
                    <div className="rwd__range rwd__range--right"
                      style={{
                        left:  `${toFrac(rb.rightRange[0])}%`,
                        width: `${toFrac(rb.rightRange[1]) - toFrac(rb.rightRange[0])}%`,
                        top: `${14 + ri * 10}px`,
                      }}/>
                  )}
                </div>
              ))}

              {/* Wall markers */}
              {walls.map(w => (
                <div key={w}
                  className={`rwd__wall ${hitWalls.includes(w) ? 'rwd__wall--hit' : ''}`}
                  style={{left: `${toFrac(w)}%`}}>
                  <div className="rwd__wall-bar"/>
                  <div className="rwd__wall-lbl">{w}</div>
                </div>
              ))}

              {/* Robot markers */}
              {positions.map((rb, ri) => (
                <div key={ri} className={`rwd__robot ${rb.dir ? `rwd__robot--${rb.dir.toLowerCase()}` : ''}`}
                  style={{left: `${toFrac(rb.pos)}%`}}>
                  <div className="rwd__robot-body">
                    {rb.dir ? (rb.dir==='L' ? '←' : '→') : '?'}
                  </div>
                  <div className="rwd__robot-info">d={rb.d}</div>
                </div>
              ))}
            </div>

            {/* DP state table */}
            {dpState && (
              <div className="rwd__dp">
                <div className="rwd__dp-hd">DP state</div>
                <div className="rwd__dp-row">
                  <span className="rwd__dp-lbl">shoot LEFT</span>
                  <span className="rwd__dp-val rwd__dp-val--l">
                    {dpState.curL !== undefined ? dpState.curL : dpState.subL}
                  </span>
                </div>
                <div className="rwd__dp-row">
                  <span className="rwd__dp-lbl">shoot RIGHT</span>
                  <span className="rwd__dp-val rwd__dp-val--r">
                    {dpState.curR !== undefined ? dpState.curR : dpState.subR}
                  </span>
                </div>
                {dpState.formula && (
                  <div className="rwd__dp-formula">{dpState.formula}</div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════
          ROBOT PATH DP  —  #3259
          3-layer DP grid: dp[i][j][k] = max coins from
          (0,0) to (i,j) with k neutralizations remaining.
          Shows all 3 k-layers side by side, active cell
          highlighted in orange, filled cells teal.
          Path trace shown on the active layer.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'robot_path_dp' && (() => {
        const coins   = cur.coins   || [];   // 2D grid values
        const tables  = cur.tables  || [];   // tables[k] = 2D dp array for k remaining
        const active  = cur.active  || null; // [i,j] currently filling
        const kLayer  = cur.k_layer ?? 2;    // which k is being highlighted
        const path    = cur.path    || [];   // [[i,j],...] best path so far
        const note    = cur.note    || '';
        const m       = coins.length;
        const n       = coins[0]?.length || 0;
        const NEG_INF = -Infinity;

        const cellVal = (k, i, j) => {
          const v = tables[k]?.[i]?.[j];
          return (v === null || v === undefined || v <= -1e8) ? null : v;
        };

        const isActive = (i,j) => active && active[0]===i && active[1]===j;
        const onPath   = (i,j) => path.some(p => p[0]===i && p[1]===j);

        const kColors = ['var(--tx3)','var(--blue)','var(--accent)'];
        const kBgs    = ['var(--bg3)','var(--blue-dim)','var(--accent-dim)'];
        const kBds    = ['var(--bd2)','rgba(126,184,247,.3)','var(--accent-mid)'];

        return (
          <div className="rpd">
            {/* ── Coin grid ── */}
            <div className="rpd__section">
              <div className="rpd__sec-lbl">coins grid</div>
              <div className="rpd__grid" style={{gridTemplateColumns:`repeat(${n},40px)`}}>
                {coins.map((row,i) => row.map((v,j) => (
                  <div key={`${i}-${j}`}
                    className={[
                      'rpd__coin',
                      v < 0  ? 'rpd__coin--neg' : v > 0 ? 'rpd__coin--pos' : 'rpd__coin--zero',
                      onPath(i,j) ? 'rpd__coin--path' : '',
                    ].join(' ')}>
                    {v}
                  </div>
                )))}
              </div>
            </div>

            {/* ── DP layers ── */}
            <div className="rpd__layers">
              {[0,1,2].map(k => (
                <div key={k} className={`rpd__layer ${k===kLayer?'rpd__layer--active':''}`}>
                  <div className="rpd__layer-lbl" style={{color:kColors[k],borderColor:kBds[k],background:kBgs[k]}}>
                    dp[i][j][{k}]  — {k} neutralization{k!==1?'s':''} remaining
                  </div>
                  <div className="rpd__grid" style={{gridTemplateColumns:`repeat(${n},40px)`}}>
                    {Array.from({length:m},(_,i) => Array.from({length:n},(_,j) => {
                      const v     = cellVal(k,i,j);
                      const actv  = k===kLayer && isActive(i,j);
                      const fill  = v !== null;
                      return (
                        <div key={`${i}-${j}`}
                          className={[
                            'rpd__cell',
                            actv  ? 'rpd__cell--active' : '',
                            fill && !actv ? `rpd__cell--fill-${k}` : '',
                            v === null ? 'rpd__cell--empty' : '',
                          ].join(' ')}>
                          {v !== null ? v : '·'}
                        </div>
                      );
                    }))}
                  </div>
                </div>
              ))}
            </div>

            {/* ── Recurrence callout ── */}
            {cur.recurrence && (
              <div className="rpd__rec">
                <span className="rpd__rec-icon">↗</span>
                <code className="rpd__rec-code">{cur.recurrence}</code>
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════
          ROBOT COLLISION  —  #2751
          Shows robots sorted by position on a number line.
          Stack of R-movers highlighted; active collision
          shown with flashing border; destroyed robots fade.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'robot_collision' && (() => {
        const robots  = cur.robots  || [];   // [{id,pos,dir,health,state}]
        const stack   = cur.stack   || [];   // ids of R-movers on stack
        const phase   = cur.phase   || '';
        const isDone  = cur.done;

        const stateColor = s =>
          s === 'dead'      ? 'var(--hard)'    :
          s === 'active'    ? 'var(--orange)'  :
          s === 'stack'     ? 'var(--blue)'    :
          s === 'survivor'  ? 'var(--accent)'  : 'var(--tx3)';
        const stateBg = s =>
          s === 'dead'      ? 'rgba(240,113,120,.1)'  :
          s === 'active'    ? 'rgba(255,140,60,.12)'  :
          s === 'stack'     ? 'var(--blue-dim)'        :
          s === 'survivor'  ? 'var(--accent-dim)'      : 'var(--bg4)';
        const stateBd = s =>
          s === 'dead'      ? 'rgba(240,113,120,.35)'  :
          s === 'active'    ? 'rgba(255,140,60,.5)'    :
          s === 'stack'     ? 'rgba(126,184,247,.35)'  :
          s === 'survivor'  ? 'var(--accent-mid)'      : 'var(--bd)';

        return (
          <div className="rc">
            {/* Phase badge */}
            <div className="rc__hd">
              <span className="rc__phase">{phase}</span>
              {isDone && (
                <span className="rc__done-pill">
                  {robots.filter(r=>r.state!=='dead').length} survivor{robots.filter(r=>r.state!=='dead').length!==1?'s':''}
                </span>
              )}
            </div>

            {/* Robot row */}
            <div className="rc__row">
              {robots.map(r => (
                <div key={r.id} className={`rc__robot ${r.state==='dead'?'rc__robot--dead':''}`}
                  style={{borderColor: stateBd(r.state), background: stateBg(r.state)}}>
                  <div className="rc__robot-dir" style={{color: stateColor(r.state)}}>
                    {r.state==='dead' ? '💥' : r.dir==='R' ? '→' : '←'}
                  </div>
                  <div className="rc__robot-h" style={{color: stateColor(r.state)}}>
                    {r.state==='dead' ? 0 : r.health}
                  </div>
                  <div className="rc__robot-pos">[{r.id}]</div>
                </div>
              ))}
            </div>

            {/* Stack display */}
            <div className="rc__stack-wrap">
              <span className="rc__stack-lbl">R-stack</span>
              <div className="rc__stack">
                {stack.length === 0
                  ? <span className="rc__stack-empty">empty</span>
                  : stack.map(id => {
                    const r = robots.find(x => x.id === id);
                    return (
                      <div key={id} className="rc__stack-chip">
                        R{id} h={r?.health ?? '?'}
                      </div>
                    );
                  })
                }
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════
          GRID PARTITION  —  #3548
          Shows the grid with a horizontal or vertical cut
          highlighted, sums on each side, and which cell
          (if any) is being discarded to equalise the halves.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'grid_partition' && (() => {
        const grid      = cur.grid || [];
        const phase     = cur.phase || '';
        const cutDir    = cur.cut_dir;
        const cutPos    = cur.cut_pos;
        const hlRow     = cur.highlight_row;
        const hlCol     = cur.highlight_col;
        const topSum    = cur.top_sum;
        const botSum    = cur.bot_sum;
        const leftSum   = cur.left_sum;
        const rightSum  = cur.right_sum;
        const diff      = cur.diff;
        const discard   = cur.discard_cell;   // [row, col]
        const discardV  = cur.discard_val;
        const isDone    = cur.done;
        const m = grid.length;
        const n = grid[0]?.length || 0;

        const phaseColor = phase === 'complete'  ? 'var(--accent)'
                         : phase === 'h-cut scan' || phase === 'h-cut valid' ? 'var(--blue)'
                         : phase === 'v-cut scan' ? 'var(--orange)'
                         : 'var(--tx3)';
        const phaseBg   = phase === 'complete'  ? 'var(--accent-dim)'
                         : phase === 'h-cut scan' || phase === 'h-cut valid' ? 'var(--blue-dim)'
                         : phase === 'v-cut scan' ? 'rgba(255,140,60,.1)'
                         : 'var(--bg3)';
        const phaseBd   = phase === 'complete'  ? 'var(--accent-mid)'
                         : phase === 'h-cut scan' || phase === 'h-cut valid' ? 'rgba(126,184,247,.3)'
                         : phase === 'v-cut scan' ? 'rgba(255,140,60,.3)'
                         : 'var(--bd2)';

        return (
          <div className="gp">
            {/* Phase pill */}
            <div className="gp__hd">
              <span className="gp__phase-pill" style={{color:phaseColor,background:phaseBg,borderColor:phaseBd}}>
                {phase}
              </span>
              {isDone && cur.result === true && (
                <span className="gp__result-pill">✓ true</span>
              )}
              {isDone && cur.result === false && (
                <span className="gp__result-pill gp__result-pill--false">✗ false</span>
              )}
            </div>

            {/* Grid + cut visualisation */}
            <div className="gp__grid-wrap">
              <div className="gp__grid">
                {grid.map((row, ri) => (
                  <div key={ri} className="gp__row">
                    {row.map((val, ci) => {
                      const isCutH  = cutDir === 'horizontal' && ri === cutPos;
                      const isCutV  = cutDir === 'vertical'   && ci === cutPos;
                      const isTop   = cutDir === 'horizontal' && ri <= (cutPos ?? -1);
                      const isLeft  = cutDir === 'vertical'   && ci <= (cutPos ?? -1);
                      const isDiscard = discard && discard[0] === ri && discard[1] === ci;
                      return (
                        <div
                          key={ci}
                          className={[
                            'gp__cell',
                            isCutH  ? 'gp__cell--cut-h'  : '',
                            isCutV  ? 'gp__cell--cut-v'  : '',
                            isTop   ? 'gp__cell--top'    : cutDir === 'horizontal' ? 'gp__cell--bot' : '',
                            isLeft  ? 'gp__cell--left'   : cutDir === 'vertical'   ? 'gp__cell--right' : '',
                            isDiscard ? 'gp__cell--discard' : '',
                          ].join(' ')}
                        >
                          {val}
                          {isDiscard && <span className="gp__cell-x">✕</span>}
                        </div>
                      );
                    })}
                  </div>
                ))}

                {/* Cut line overlay */}
                {cutDir === 'horizontal' && cutPos != null && (
                  <div className="gp__cut-h" style={{ top: `${((cutPos + 1) / m) * 100}%` }} />
                )}
                {cutDir === 'vertical' && cutPos != null && (
                  <div className="gp__cut-v" style={{ left: `${((cutPos + 1) / n) * 100}%` }} />
                )}
              </div>

              {/* Sum annotations */}
              {(topSum != null || botSum != null) && (
                <div className="gp__sums">
                  {topSum != null && (
                    <div className="gp__sum gp__sum--top">
                      <span className="gp__sum-lbl">top</span>
                      <span className="gp__sum-val">{topSum}</span>
                    </div>
                  )}
                  {botSum != null && (
                    <div className="gp__sum gp__sum--bot">
                      <span className="gp__sum-lbl">bot</span>
                      <span className="gp__sum-val">{botSum}</span>
                    </div>
                  )}
                  {leftSum != null && (
                    <div className="gp__sum gp__sum--left">
                      <span className="gp__sum-lbl">left</span>
                      <span className="gp__sum-val">{leftSum}</span>
                    </div>
                  )}
                  {rightSum != null && (
                    <div className="gp__sum gp__sum--right">
                      <span className="gp__sum-lbl">right</span>
                      <span className="gp__sum-val">{rightSum}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Diff / discard row */}
            {diff != null && (
              <div className="gp__diff-row">
                <div className="gp__diff-pill">
                  <span className="gp__diff-lbl">diff</span>
                  <span className="gp__diff-val">{diff}</span>
                </div>
                {discard && (
                  <div className="gp__discard-pill">
                    <span className="gp__diff-lbl">discard</span>
                    <span className="gp__diff-val">grid[{discard[0]}][{discard[1]}] = {discardV}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════
          FANCY SEQUENCE  —  #1622
          Shows sequence state as value cells + two global
          state pills (mult_acc, add_acc) per step.
          Highlights the active index on getIndex calls,
          highlights newly appended element on append,
          and shows the lazy formula being computed.
      ════════════════════════════════════════════════════ */}
      {cur.type === 'fancy_seq' && (() => {
        const seq       = cur.seq       || [];
        const op        = cur.op        || '';
        const multAcc   = cur.mult_acc  ?? 1;
        const addAcc    = cur.add_acc   ?? 0;
        const result    = cur.result;
        const formula   = cur.formula;
        const activeIdx = cur.active_idx;
        const isGet     = op.startsWith('getIndex');
        const isAppend  = op.startsWith('append');
        const isAdd     = op.startsWith('addAll');
        const isMult    = op.startsWith('multAll');
        const isInit    = op === 'Fancy()';
        const isReturn  = result && result !== 'null';

        // Operation badge color
        const opColor = isGet    ? 'var(--accent)' :
                        isAppend ? 'var(--blue)'   :
                        isAdd    ? 'var(--gold)'   :
                        isMult   ? 'var(--orange)' :
                                   'var(--tx3)';
        const opBg    = isGet    ? 'var(--accent-dim)' :
                        isAppend ? 'var(--blue-dim)'   :
                        isAdd    ? 'rgba(245,201,106,.1)' :
                        isMult   ? 'rgba(255,140,60,.1)'  :
                                   'var(--bg3)';
        const opBd    = isGet    ? 'var(--accent-mid)' :
                        isAppend ? 'rgba(126,184,247,.3)' :
                        isAdd    ? 'rgba(245,201,106,.3)'  :
                        isMult   ? 'rgba(255,140,60,.3)'   :
                                   'var(--bd2)';

        return (
          <div className="fq">
            {/* ── Operation header ── */}
            <div className="fq__hd">
              <div className="fq__op-pill" style={{color:opColor, background:opBg, borderColor:opBd}}>
                {op}
              </div>
              {isReturn && (
                <div className="fq__return">
                  <span className="fq__return-lbl">returns</span>
                  <span className="fq__return-val" style={{color: result==='-1' ? 'var(--hard)' : 'var(--accent)'}}>
                    {result}
                  </span>
                </div>
              )}
            </div>

            {/* ── Sequence cells ── */}
            <div className="fq__seq-wrap">
              <span className="fq__seq-lbl">sequence</span>
              {seq.length === 0 ? (
                <span className="fq__seq-empty">[ empty ]</span>
              ) : (
                <div className="fq__seq">
                  {seq.map((v, i) => {
                    const isActive  = i === activeIdx;
                    const isNewApp  = isAppend && i === seq.length - 1;
                    return (
                      <div key={i} className={[
                        'fq__cell',
                        isActive  ? 'fq__cell--active' : '',
                        isNewApp  ? 'fq__cell--new'    : '',
                        isAdd     ? 'fq__cell--add'    : '',
                        isMult    ? 'fq__cell--mult'   : '',
                      ].join(' ')}>
                        <span className="fq__cell-val">{v}</span>
                        <span className="fq__cell-idx">[{i}]</span>
                        {isNewApp && <span className="fq__cell-tag">new</span>}
                        {isActive && !isNewApp && <span className="fq__cell-tag fq__cell-tag--get">← ans</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Global state pills ── */}
            <div className="fq__state">
              <div className={`fq__state-pill ${isMult ? 'fq__state-pill--changed' : ''}`}>
                <span className="fq__state-lbl">mult_acc</span>
                <span className="fq__state-val fq__state-val--mult">{multAcc}</span>
              </div>
              <div className={`fq__state-pill ${(isAdd || isMult) ? 'fq__state-pill--changed' : ''}`}>
                <span className="fq__state-lbl">add_acc</span>
                <span className="fq__state-val fq__state-val--add">{addAcc}</span>
              </div>
              <div className="fq__state-pill fq__state-pill--formula">
                <span className="fq__state-lbl">formula</span>
                <span className="fq__state-val fq__state-val--formula">actual[i] = raw[i] × mult_acc + add_acc</span>
              </div>
            </div>

            {/* ── Formula callout for this step ── */}
            {formula && (
              <div className="fq__formula">
                <span className="fq__formula-icon">
                  {isGet ? '🔍' : isAppend ? '✏️' : isAdd ? '➕' : '✖️'}
                </span>
                <code className="fq__formula-code">{formula}</code>
              </div>
            )}

          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════
          HAPPY STRING TREE  —  #1415
          Shows the trie of happy strings being built:
          - Top: string built so far, character by character
          - Middle: choice row — which chars are valid at
            this position, which one was picked (highlighted)
          - Bottom: "why" explanation as a clean callout
      ════════════════════════════════════════════════════ */}
      {cur.type === 'happy_tree' && (() => {
        const built   = cur.built   || '';
        const choices = cur.choices || [];
        const picked  = cur.picked  || '';
        const n       = cur.n       || 3;
        const pos     = cur.pos;
        const branch  = cur.branch;
        const kVal    = cur.k_val;
        const idx     = cur.idx;
        const isDone  = cur.done;

        return (
          <div className="viz__frame">
            <div className="viz__frame-hd">
              {isDone
                ? `✅ Happy string found: "${built}"`
                : pos === null
                  ? `Setup — n=${n}, total = 3 × 2^(n-1) strings`
                  : `Position ${pos} of ${n-1} — picking character ${pos+1}/${n}`}
            </div>

            {/* ── String being built ── */}
            <div className="hst__built-row">
              <span className="hst__built-lbl">Built so far</span>
              <div className="hst__chars">
                {Array.from({length: n}, (_,i) => {
                  const ch = built[i];
                  const isActive = i === pos && !isDone;
                  const isFilled = i < built.length && !(isActive);
                  return (
                    <div key={i} className={`hst__char-box ${isFilled ? 'hst__char-box--filled' : ''} ${isActive ? 'hst__char-box--active' : ''} ${isDone ? 'hst__char-box--done' : ''}`}>
                      <span className="hst__char-val">{ch || '?'}</span>
                      <span className="hst__char-idx">[{i}]</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Choices row (only during building) ── */}
            {pos !== null && !isDone && choices.length > 0 && (
              <div className="hst__choices-wrap">
                <span className="hst__choices-lbl">
                  {pos === 0 ? 'All 3 chars valid (first position)' : `Valid chars (≠ '${built[pos-1] || '?'}'):`}
                </span>
                <div className="hst__choices">
                  {choices.map((c, ci) => (
                    <div key={ci} className={`hst__choice ${c === picked ? 'hst__choice--picked' : 'hst__choice--other'}`}>
                      <span className="hst__choice-char">{c}</span>
                      <span className="hst__choice-info">
                        {c === picked ? `✓ picked (idx ${idx})` : `idx ${ci}`}
                      </span>
                      {c === picked && branch != null && (
                        <span className="hst__choice-branch">{branch} strings →</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Math decode vars ── */}
            {pos !== null && !isDone && (
              <div className="hst__math-row">
                <div className="hst__math-pill">
                  <span className="hst__math-lbl">branch</span>
                  <span className="hst__math-val">2^(n−pos−1) = 2^({n}−{pos}−1) = {branch}</span>
                </div>
                <div className="hst__math-pill hst__math-pill--k">
                  <span className="hst__math-lbl">k (0-based)</span>
                  <span className="hst__math-val">{kVal}</span>
                </div>
                <div className="hst__math-pill hst__math-pill--idx">
                  <span className="hst__math-lbl">idx = k ÷ branch</span>
                  <span className="hst__math-val">{kVal} ÷ {branch} = {idx}  →  choices[{idx}] = '{picked}'</span>
                </div>
                {branch > 1 && (
                  <div className="hst__math-pill">
                    <span className="hst__math-lbl">k after = k % branch</span>
                    <span className="hst__math-val">{kVal} % {branch} = {kVal % branch}</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Setup state (pos null) ── */}
            {pos === null && (
              <div className="hst__setup-grid">
                <div className="hst__setup-pill">
                  <span className="hst__math-lbl">n</span>
                  <span className="hst__math-val">{cur.n}</span>
                </div>
                <div className="hst__setup-pill">
                  <span className="hst__math-lbl">k (input)</span>
                  <span className="hst__math-val">{cur.k_input}</span>
                </div>
                <div className="hst__setup-pill">
                  <span className="hst__math-lbl">total strings</span>
                  <span className="hst__math-val">3 × 2^{n-1} = {3 * Math.pow(2, cur.n-1)}</span>
                </div>
                <div className="hst__setup-pill hst__math-pill--idx">
                  <span className="hst__math-lbl">k (0-based)</span>
                  <span className="hst__math-val">{cur.k_val} = {cur.k_input} − 1</span>
                </div>
              </div>
            )}

          </div>
        );
      })()}

      {/* ═══════════════════════════════════════════════════
          GENERIC VARS  —  #868 #1356 #1536 #2657 etc.
          Skipped for tree + binary_sim (they handle their
          own vars inside viz__frame above).
      ════════════════════════════════════════════════════ */}
{/* ═══════════════════════════════════════════════════
    STRIVER CUSTOM VISUALIZERS
════════════════════════════════════════════════════ */}

{cur.type === 'matrix_zero_spread' && (() => {
  const cols = cur.matrix?.[0]?.length ?? 1;
  return (
    <div className="viz__mzs">
      <div className="viz__mzs-phase">{cur.phase}</div>

      <div className="viz__mzs-col0">
        col0 flag = <strong>{cur.col0 === 0 ? '0 → first column will be zeroed' : '1 → first column safe'}</strong>
      </div>

      <div
        className="viz__mzs-grid"
        style={{ gridTemplateColumns: `repeat(${cols}, 56px)` }}
      >
        {cur.matrix?.map((row, r) =>
          row.map((cell, c) => {
            const focused   = cur.focus?.some(([fr, fc]) => fr === r && fc === c);
            const modified  = cur.modified?.some(([fr, fc]) => fr === r && fc === c);
            const isZero    = cell === 0;
            return (
              <div
                key={`${r}-${c}`}
                className={[
                  'viz__mzs-cell',
                  focused  ? 'viz__mzs-cell--focus'    : '',
                  modified ? 'viz__mzs-cell--modified'  : '',
                  isZero && !focused && !modified ? 'viz__mzs-cell--zero' : '',
                ].join(' ').trim()}
              >
                {cell}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
})()}

{cur.type === 'pascal_triangle_build' && (
  <div className="viz__pascal">
    <div className="viz__pascal-phase">{cur.phase}</div>

    {cur.triangle?.map((row, r) => (
      <div key={r} className="viz__pascal-row">
        {row.map((val, c) => {
          const focused = cur.focus?.some(([fr, fc]) => fr === r && fc === c);
          const parent  = cur.parents?.some(([fr, fc]) => fr === r && fc === c);
          return (
            <div
              key={c}
              className={[
                'viz__pascal-cell',
                focused ? 'viz__pascal-cell--focus'  : '',
                parent  ? 'viz__pascal-cell--parent' : '',
              ].join(' ').trim()}
              title={`row ${r}, col ${c}`}
            >
              {val}
            </div>
          );
        })}
      </div>
    ))}
  </div>
)}

{cur.type === 'next_permutation_transform' && (
  <div className="viz__np">
    <div className="viz__np-phase">{cur.phase}</div>

    <div className="viz__np-row">
      {cur.array?.map((num, idx) => {
        const isPivot      = cur.pivot === idx;
        const isSuccessor  = cur.successor === idx;
        const isModified   = cur.modified?.includes(idx);
        const isFocused    = cur.focus?.includes(idx);

        return (
          <div
            key={idx}
            className={[
              'viz__np-cell',
              isPivot     ? 'viz__np-cell--pivot'     : '',
              isSuccessor ? 'viz__np-cell--successor'  : '',
              isModified  ? 'viz__np-cell--modified'   : '',
              isFocused   ? 'viz__np-cell--focus'      : '',
            ].join(' ').trim()}
          >
            {num}
            {isPivot     && <div className="viz__np-tag viz__np-tag--pivot">Pivot</div>}
            {isSuccessor && <div className="viz__np-tag viz__np-tag--succ">Successor</div>}
          </div>
        );
      })}
    </div>
  </div>
)}

{/* ═══════════════════════════════════════════════════
    STRIVER CUSTOM VISUALIZERS
════════════════════════════════════════════════════ */}

{cur.type === 'stack_string_reverse' && (
  <div className="viz__ssr">
    <div className="viz__ssr-phase">{cur.phase}</div>

    {/* Input Sequence View */}
    <div className="viz__ssr-seq">
      {cur.chars?.map((char, idx) => {
        const isCurrent = cur.i === idx;
        const isFocus = cur.focus?.includes(idx);
        const isParen = char === '(' || char === ')';

        return (
          <div
            key={idx}
            className={[
              'viz__ssr-cell',
              isCurrent ? 'viz__ssr-cell--curr' : '',
              isFocus ? 'viz__ssr-cell--focus' : '',
              isParen ? 'viz__ssr-cell--paren' : '',
            ]
              .join(' ')
              .trim()}
          >
            {char}
            <span className="viz__ssr-idx">{idx}</span>
            {isCurrent && <div className="viz__ssr-ptr">i</div>}
          </div>
        );
      })}
    </div>

    {/* Stack & Active String Builder View */}
    <div className="viz__ssr-grid">
      <div className="viz__ssr-card">
        <div className="viz__ssr-card-hdr">Stack Content</div>
        <div className="viz__ssr-stack">
          {cur.stack?.length > 0 ? (
            cur.stack.map((item, sIdx) => (
              <span key={sIdx} className="viz__ssr-stack-item">
                "{item}"
              </span>
            ))
          ) : (
            <span className="viz__ssr-empty">Stack is empty</span>
          )}
        </div>
      </div>

      <div className="viz__ssr-card">
        <div className="viz__ssr-card-hdr">Active Builder (curr)</div>
        <div className="viz__ssr-buffer">
          {cur.curr !== undefined && cur.curr !== '' ? (
            <span className="viz__ssr-buf-str">"{cur.curr}"</span>
          ) : (
            <span className="viz__ssr-empty">"" (empty)</span>
          )}
        </div>
      </div>
    </div>
  </div>
)}

{cur.type === 'max_nesting_depth' && (
  <div className="viz__mnd">
    <div className="viz__mnd-phase">{cur.phase}</div>

    {/* Sequence Track */}
    <div className="viz__mnd-seq">
      {cur.s?.split('').map((char, idx) => {
        const isCurrent = cur.i === idx;
        const isProcessed = cur.i !== undefined && idx < cur.i;
        const isParen = char === '(' || char === ')';

        return (
          <div
            key={idx}
            className={[
              'viz__mnd-cell',
              isCurrent ? 'viz__mnd-cell--curr' : '',
              isProcessed ? 'viz__mnd-cell--past' : '',
              isParen ? 'viz__mnd-cell--paren' : '',
            ]
              .join(' ')
              .trim()}
          >
            {char}
            <span className="viz__mnd-idx">{idx}</span>
            {isCurrent && <div className="viz__mnd-ptr">▼</div>}
          </div>
        );
      })}
    </div>

    {/* Visual Depth Bar Gauge */}
    <div className="viz__mnd-gauge">
      <div className="viz__mnd-gauge-label">
        <span>Nesting Level Visualizer</span>
        <span>Current: {cur.depth ?? 0} | Peak: {cur.maxDepth ?? 0}</span>
      </div>
      <div className="viz__mnd-bar-wrap">
        <div
          className="viz__mnd-bar-current"
          style={{ width: `${Math.min(100, ((cur.depth ?? 0) / Math.max(1, cur.maxDepth ?? 1)) * 100)}%` }}
        />
        <div
          className="viz__mnd-bar-peak"
          style={{ width: `${Math.min(100, ((cur.maxDepth ?? 0) / 5) * 100)}%` }}
        />
      </div>
    </div>

    {/* Metric Cards */}
    <div className="viz__mnd-grid">
      <div className="viz__mnd-card">
        <div className="viz__mnd-card-hdr">Active Depth</div>
        <div className="viz__mnd-val">{cur.depth ?? 0}</div>
      </div>

      <div className="viz__mnd-card viz__mnd-card--highlight">
        <div className="viz__mnd-card-hdr">Peak Max Depth</div>
        <div className="viz__mnd-val viz__mnd-val--accent">{cur.maxDepth ?? 0}</div>
      </div>
    </div>
  </div>
)}

{/* ═══════════════════════════════════════════════════
    EXISTING VARS BLOCK (UNCHANGED)
════════════════════════════════════════════════════ */}

{cur.vars &&
 cur.type !== 'tree' &&
 cur.type !== 'binary_sim' &&
 cur.type !== 'dp_table' &&
 cur.type !== 'bit_xor' &&
 cur.type !== 'graph_mst' &&
 cur.type !== 'worker_bs' &&
 cur.type !== 'happy_tree' &&
 cur.type !== 'fancy_seq' &&
 cur.type !== 'grid_partition' &&
 cur.type !== 'robot_collision' &&
 cur.type !== 'robot_path_dp' &&
 cur.type !== 'robot_walls' &&
 cur.type !== 'cipher_matrix' &&
 cur.type !== 'robot_grid' &&
 cur.type !== 'jump_bfs' &&
 cur.type !== 'jump_bfs_iv' &&
 cur.type !== 'two_pointers_common' &&
 cur.type !== 'prefix_common_scan' &&
 cur.type !== 'sorted_rotated_check' &&
 cur.type !== 'dag_jump_scan' &&
 cur.type !== 'sliding_jump_window' &&
 cur.type !== 'special_chars_check' &&
 cur.type !== 'special_chars_two_check' &&
 cur.type !== 'digit_sum_min_check' &&
 cur.type !== 'block_placement_scan' &&
 cur.type !== 'asteroid_conquer_scan' &&
 cur.type !== 'matrix_zero_spread' &&
 cur.type !== 'pascal_triangle_build' &&
 cur.type !== 'next_permutation_transform' &&
 cur.type !== 'rotate_matrix_transform' &&
 cur.type !== 'merge_intervals_timeline' &&
 cur.type !== 'gap_merge_transform' &&
 cur.type !== 'kadane_transform' &&
 cur.type !== 'dnf_transform' &&
 cur.type !== 'stock_transform' &&
 cur.type !== 'floyd_cycle_duplicate' &&
 cur.type !== 'xor_repeat_missing' &&
 cur.type !== 'merge_inversion_count' &&
 cur.type !== 'stack_string_reverse' &&
 cur.type !== 'max_nesting_depth' && (
  <div className="viz__vars">
    {Object.entries(cur.vars).map(([k,v]) => {
      const changed = cur.changed?.includes(k);
      const isFinal = cur.final === k;

      return (
        <div
          key={k}
          className={`viz__var ${changed?'viz__var--changed':''} ${isFinal?'viz__var--final':''}`}
        >
          <div className="viz__var-lbl">{k}</div>
          <div className="viz__var-val">{String(v)}</div>

          {changed && (
            <div className="viz__var-up">
              ↑ updated
            </div>
          )}

          {isFinal && (
            <div
              className="viz__var-up"
              style={{color:'var(--accent)'}}
            >
              ✓ answer
            </div>
          )}
        </div>
      );
    })}
  </div>
)}

      {/* ── Note ── */}
      <div className={`viz__note ${cur.done?'viz__note--done':''}`}>{cur.note}</div>

      {/* ── Controls ── */}
      <div className="viz__ctrl">
        <button className="viz__btn viz__btn--ghost"   onClick={()=>go(s-1)} disabled={s===0}>← Prev</button>
        <button className="viz__btn viz__btn--primary" onClick={()=>go(s+1)} disabled={s===total-1}>Next →</button>
        <button className="viz__btn viz__btn--reset"   onClick={()=>go(0)}>↺ reset</button>
      </div>

      {/* ── Result ── */}
      {cur.done && (
        <div className="viz__result">
          <div className="viz__result-lbl">Result</div>
          <div className="viz__result-val">{cur.result_text}</div>
        </div>
      )}

    </div>
  );
}

/* ── main page ── */
export default function ProblemPage() {
  const { slug } = useParams();
  const { problem, loading, error } = useProblem(slug);
  const [tab, setTab] = useState('python');
  const sc = problem?.solution_content;

  // Normalise code field: old schema uses sc.codes, new schema uses sc.solutions
  const codes = sc?.codes ?? sc?.solutions ?? {};

  // Normalise complexity.
  // Preferred schema (all problems): sc.time_complexity = { value, note }
  // Fallback: sc.complexity = { time: "O(...) — note", space: "O(...) — note" }
  //           auto-splits on first " — " to separate value from note.
  function splitCx(raw = '') {
    const i = raw.indexOf(' — ');
    return i > -1 ? { value: raw.slice(0, i), note: raw.slice(i + 3) } : { value: raw, note: '' };
  }
  const timeComplexity  = sc?.time_complexity
    ?? (sc?.complexity?.time  ? splitCx(sc.complexity.time)  : null);
  const spaceComplexity = sc?.space_complexity
    ?? (sc?.complexity?.space ? splitCx(sc.complexity.space) : null);

  return (
    <main className="prob">
      <div className="container prob__wrap">
        <TableOfContents/>

        <div className="prob__top">
          <Link to="/potd" className="prob__back">← All Problems</Link>
          {!loading && problem && (
            <a href={problem.lc_link} target="_blank" rel="noreferrer" className="prob__lc">LeetCode ↗</a>
          )}
        </div>

        <div className="prob__hd">
          {loading ? (
            <><SK h="13px" w="160px" mb="10px"/><SK h="30px" w="80%"/></>
          ) : error ? (
            <div className="prob__err">⚠ Could not load — check your Supabase environment variables.</div>
          ) : (
            <>
              <div className="prob__meta">
                <span className="prob__date">POTD · {problem.date}</span>
                <span className={`prob__badge prob__badge--${problem.difficulty?.toLowerCase()}`}>{problem.difficulty}</span>
                {problem.tags?.map(t=><span key={t} className="prob__badge prob__badge--tag">{t}</span>)}
              </div>
              <h1 className="prob__title"><span className="prob__num">#{problem.id} </span>{problem.title}</h1>
            </>
          )}
        </div>

{/* ── Explanatory Video ── */}
{!loading && (
  <section id="sec-video" className="ps">
    <h2 className="ps__h">Explanatory Video</h2>
    {sc?.video_url ? (
      <div className="vid__wrap">
        <iframe
          className="vid__frame"
          src={`https://www.youtube.com/embed/${sc.video_url}`}
          title="Explanatory Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    ) : (
      <div className="vid__soon vid__soon--faded">
        <div className="vid__soon-icon">▶</div>
        <div className="vid__soon-text">
          <span className="vid__soon-title">Video walkthrough coming soon</span>
          <span className="vid__soon-sub">
            A full explanation video for this problem will be added here.
          </span>
        </div>
      </div>
    )}
  </section>
)}

        {/* ── What Are We Doing ── */}
        <section id="sec-what" className="ps">
          <h2 className="ps__h">What Are We Actually Doing?</h2>
          <div className="ps__card">
            {loading ? <><SK/><SK w="88%"/><SK w="70%"/></> : (
              sc?.what?.split('\n').filter(l => l.trim()).map((line, i) => (
                <p key={i}>{line}</p>
              ))
            )}
          </div>
        </section>

        {/* ── Underlying Concept ── */}
        <section id="sec-concept" className="ps">
          <h2 className="ps__h">Underlying Concept</h2>
          <div className="ps__card">
            {loading ? <><SK/><SK w="80%"/></> : (
              <>
                {sc?.concept_ops?.length > 0 && (
                  <div className="ops">
                    {sc.concept_ops.map((op,i) => (
                      <div key={i} className="op" data-i={`0${i+1}`}>
                        <div className="op__code">{op.code}</div>
                        <div className="op__desc">{op.desc}</div>
                      </div>
                    ))}
                  </div>
                )}
                {sc?.concept?.length > 0 && (
                  <div className="concept-points">
                    {sc.concept.map((line,i) => {
                      const dotIdx = line.indexOf('.');
                      const title = dotIdx > 0 ? line.slice(0, dotIdx + 1) : line;
                      const rest  = dotIdx > 0 ? line.slice(dotIdx + 1).trim() : '';
                      return (
                        <div key={i} className="concept-point">
                          <div className="concept-point__num">
                            <span className="concept-point__num-dig">{i+1}</span>
                            <span className="concept-point__num-line"/>
                          </div>
                          <div className="concept-point__text">
                            <strong>{title}</strong>
                            {rest && <span>{' '}{rest}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* ── Pattern Recognition ── */}
        <section id="sec-pattern" className="ps">
          <h2 className="ps__h">Pattern Recognition</h2>
          <div className="ps__card">
            {loading ? <><SK/><SK w="75%"/></> : (
              <>
                <div className="ps__pattern-name">
                  <span className="ps__pattern-name-lbl">Pattern</span>
                  <span className="ps__pattern-name-val">{problem?.pattern}</span>
                </div>
                <div className="ps__list-header">Look for these signals in the problem</div>
                <ul className="ps__list">
                  {sc?.pattern_signals?.map((s,i) => <li key={i}>{s}</li>)}
                </ul>
              </>
            )}
          </div>
          {!loading && sc && (
            <div className="tmpl">
              <div className="tmpl__lbl">Pattern Being Used (Template)</div>
              <p className="tmpl__desc">
                Any problem involving{' '}
                {sc.template_keywords?.map((kw,i,arr) => (
                  <span key={i}><span className="kw">{kw}</span>{i<arr.length-1?', ':''}</span>
                ))}{' '}follows this skeleton. The structure stays fixed — only the inner logic changes.
              </p>
              <div className="tmpl__pseudo-lbl">General template</div>
              <CodeBlock code={sc.template_pseudocode} language="python"/>
              <div className="tmpl__variants-lbl">What changes per problem</div>
              <div className="tmpl__variants">
                {sc.template_variants?.map((v,i) => (
                  <div key={i} className={`tmpl__v ${v.active?'tmpl__v--on':''}`}>
                    <span className="tmpl__v-goal">{v.goal}{v.active&&<span className="tmpl__v-this">(this problem)</span>}</span>
                    <code className="tmpl__v-code">{v.code}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

<section id="sec-visual" className="ps">
  <h2 className="ps__h">Visual Execution Flow</h2>

  <div className="ps__card ps__card--pad">
    {loading ? (
      <SK h="180px" />
    ) : (
      <Visual steps={sc?.walkthrough_steps} />
    )}
  </div>
</section>

        <section id="sec-code" className="ps">
          <h2 className="ps__h">Code</h2>
          <div className="ps__card ps__card--nop">
            <div className="ctabs">
              {['python','cpp','java'].map(l => (
                <button key={l} className={`ctab ${tab===l?'ctab--on':''}`} onClick={()=>setTab(l)}>
                  {l==='python'?'Python':l==='cpp'?'C++':'Java'}
                </button>
              ))}
            </div>
            {loading
              ? <div style={{padding:'18px'}}><SK h="150px"/></div>
              : <CodeBlock code={codes[tab]||''} language={tab}/>
            }
          </div>
        </section>

        <section id="sec-complexity" className="ps">
          <h2 className="ps__h">Time & Space Complexity</h2>
          {loading ? <div className="cx__grid"><SK h="90px"/><SK h="90px"/></div> : (
            <div className="cx__grid">
              <div className="cx__card">
                <div className="cx__icon">⏱</div>
                <div className="cx__lbl">Time</div>
                <div className="cx__val">{timeComplexity?.value}</div>
                {timeComplexity?.note && <div className="cx__note">{timeComplexity.note}</div>}
              </div>
              <div className="cx__card">
                <div className="cx__icon">💾</div>
                <div className="cx__lbl">Space</div>
                <div className="cx__val">{spaceComplexity?.value}</div>
                {spaceComplexity?.note && <div className="cx__note">{spaceComplexity.note}</div>}
              </div>
            </div>
          )}
        </section>

        {/* ── Gotchas / Common Mistakes — shown only if present in data ── */}
        {!loading && sc?.gotchas?.length > 0 && (
          <section className="ps">
            <h2 className="ps__h">Common Mistakes & Gotchas</h2>
            <div className="ps__card ps__card--pad">
              <ul className="gtch__list">
                {sc.gotchas.map((g, i) => (
                  <li key={i} className="gtch__item">
                    <span className="gtch__icon">⚠️</span>
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {(loading || sc?.similar?.length > 0) && (
        <section id="sec-similar" className="ps">
          <h2 className="ps__h">Problems Using This Same Pattern</h2>
          {loading ? (
            <div className="sim__grid"><SK h="80px"/><SK h="80px"/><SK h="80px"/></div>
          ) : (
            <div className="sim__grid">
              {sc?.similar?.map(p => (
                <a key={p.id} href={p.link} target="_blank" rel="noreferrer" className="sim__card">
                  <div className="sim__top">
                    <span className="sim__id">#{p.id}</span>
                    <span className={`sim__badge sim__badge--${p.diff?.toLowerCase()}`}>{p.diff}</span>
                    <span className="sim__arr">↗</span>
                  </div>
                  <div className="sim__name">{p.name}</div>
                  <div className="sim__note">{p.note}</div>
                </a>
              ))}
            </div>
          )}
        </section>
        )}

        <div className="prob__foot">
          <Link to="/potd" className="prob__back-lnk">← Back to all problems</Link>
        </div>
      </div>{/* end prob__wrap */}
    </main>
  );
}
